// src/components/auth/Register/Register.js
// ============================================================================
// TelcoFair — Registration (Complete with Mandatory Subscription)
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Alert,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton,
    Divider,
    CircularProgress,
    LinearProgress,
    alpha,
    Popover,
    Autocomplete,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardContent,
    Avatar,
    MenuList,
    MenuItem,
} from '@mui/material';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    Visibility,
    VisibilityOff,
    Business as BusinessIcon,
    People as PeopleIcon,
    Verified as VerifiedIcon,
    Lock as LockIcon,
    Storefront as StorefrontIcon,
    Language as LanguageIcon,
    Group as GroupIcon,
    Public as PublicIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Security as SecurityIcon,
    BadgeOutlined as WorkIcon,
    LocationOn as MapIcon,
    RocketLaunch as RocketIcon,
    ImageOutlined as AddPhotoIcon,
    Flag as FlagIcon,
    Business as DomainIcon,
    Stars as StarsIcon,
    Diamond as DiamondIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    Image as ImageIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Check as CheckIcon,
    Handshake as HandshakeIcon,
    PersonAdd as PersonAddIcon,
    Inventory2 as CategoryIcon,
    Pending as PendingIcon,
    Payment as PaymentIcon,
    CreditCard as CreditCardIcon,
    Receipt as ReceiptIcon,
    AccountBalance as AccountBalanceIcon,
    Upload as UploadIcon,
    Warning as WarningIcon,
    ReceiptLong as ReceiptLongIcon,
    Subscriptions as SubscriptionsIcon,
    Star as StarIcon,
    WorkspacePremium as PackageIcon,
    EmojiEvents as EmojiEventsIcon,
    Download as DownloadIcon,
    Description as FileCopyIcon,
    ArrowForward as ArrowForwardIosIcon,
    Add as AddIcon,
    Description as DescriptionIcon,
    AdminPanelSettings as AdminPanelSettingsIcon,
    OpenInNewRounded as OpenInNewIcon,
    GraphicEq as SignalIcon,
    NetworkCheck as CellTowerIcon,
    SettingsApplications as HubIcon,
    NetworkCheck as RouterIcon,
    Cloud as CloudIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as SignalBarsIcon,
    Speed as SpeedIcon,
    Person as PersonIcon,
    Description as TitleIcon,
    LocationOn as LocationOnIcon,
    ShoppingBagRounded as ShoppingCartIcon,
    ShoppingBagRounded as SellIcon,
    CheckCircle as CheckBoxIcon,
    PhotoCamera as PhotoCameraIcon,
    CloudUpload as CloudUploadIcon,
    LinkedIn as LinkedInIcon, // NEW: LinkedIn icon
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { safeExternalUrl } from '../../../utils/safeNavigation';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth } from '../AuthContext';
import BrandLogo from '../../common/BrandLogo';
import { db, auth } from '../../../config/firebase';
import {
    collection,
    getDocs,
    query,
    where,
    doc,
    getDoc,
    addDoc,
    serverTimestamp,
    orderBy,
    updateDoc,
    setDoc,
    deleteDoc,
} from 'firebase/firestore';
import { 
    createUserWithEmailAndPassword, 
    sendEmailVerification,
    signInWithEmailAndPassword,
    signOut,
} from 'firebase/auth';
import { startRegistrationPayment, getRegistrationPaymentStatus } from '../../../services/integrations/registrationPayment';
import { reconcileApprovedOnlineRegistrationReceipt, sendConfirmedOnlineReceipt } from '../../../services/integrations/manualPayment';
import PaymentMethodDialog from '../../payments/PaymentMethodDialog';
import { createRegistrationCompany, joinExistingCompany, saveRegistrationProgress, getRegistrationProgress, sendProfessionalVerificationEmail, getPublicPlatformRuntime, submitExtraSeatPayment } from '../../../services/integrations/backend';
import { uploadFile, validateUploadFile } from '../../../services/integrations/storage';
import { friendlyError } from '../../../utils/userMessages';

// ============================================
// PALETTE
// ============================================
// Keep a page-local alias so every style helper in this large registration
// page resolves the same shared TelcoFair palette. This also prevents the
// accidental @mui/material `palette` import that caused the build failure.
const palette = Object.freeze({
    canvas: '#F1F6F9',
    canvasAlt: '#F7FAFC',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#445168',
    inkFaint: '#92A0AF',
    inkLabel: '#334056',
    rust: '#2E6F8E',
    rustDeep: '#234F68',
    pine: '#42B89B',
    gold: '#7C5CF2',
    goldDeep: '#5C3ED0',
    blue: '#2F6FED',
    purple: '#8B4DE8',
    teal: '#0F9E9A',
    tealDeep: '#087570',
    green: '#13A773',
    greenDeep: '#0A7B55',
    danger: '#D94B5A',
    warning: '#E29A20',
    linkedin: '#0A66C2',
});


const SHADOW = '25, 57, 78';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BOUNCE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// ============================================================================
// ELEVATION LADDER
// ============================================================================

const ELEVATION = {
    0: { ambY: 2,  ambB: 8,   ambA: 0.05, conY: 1,  conB: 2,  conA: 0.04, blur: 8,  rake: 0.5,  fill: 0.34 },
    1: { ambY: 8,  ambB: 24,  ambA: 0.09, conY: 2,  conB: 6,  conA: 0.05, blur: 14, rake: 0.68, fill: 0.5 },
    2: { ambY: 16, ambB: 44,  ambA: 0.12, conY: 4,  conB: 12, conA: 0.07, blur: 20, rake: 0.82, fill: 0.62 },
    3: { ambY: 28, ambB: 70,  ambA: 0.15, conY: 7,  conB: 20, conA: 0.08, blur: 26, rake: 0.92, fill: 0.7 },
    4: { ambY: 42, ambB: 104, ambA: 0.18, conY: 11, conB: 30, conA: 0.09, blur: 32, rake: 1,    fill: 0.78 },
};

// ============================================================================
// GLASS STYLES
// ============================================================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: '#FFFFFF',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color,
    background: alpha(color, .07),
    border: `1px solid ${alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? '#fff' : color,
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassButton = (color, deep) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    color: '#fff !important',
    '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#fff !important' },
    minHeight: 34,
    paddingInline: 12,
    background: `linear-gradient(135deg, ${color}, ${deep || color})`,
    border: `1px solid ${alpha(color, .12)}`,
    boxShadow: `0 5px 14px ${alpha(color, .16)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 18px ${alpha(color, .20)}` },
    '&.Mui-disabled': { opacity: .58, color: '#fff' },
});

const glassGhostButton = (color) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    minHeight: 34,
    color,
    background: 'rgba(255,253,250,.92)',
    border: `1px solid ${alpha(color, .15)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.03)',
    transition: 'background .18s ease, border-color .18s ease',
    '&:hover': { background: alpha(color, .035), borderColor: alpha(color, .22) },
});

// ============================================================================
// GLOBAL CSS
// ============================================================================

const GLOBAL_CSS = `
    @keyframes tfAuroraA {
        0%, 100% { transform: translate3d(0,0,0) scale(1);           opacity: 0.55; }
        33%      { transform: translate3d(60px,-40px,0) scale(1.18); opacity: 0.85; }
        66%      { transform: translate3d(-40px,30px,0) scale(0.92); opacity: 0.5; }
    }
    @keyframes tfAuroraB {
        0%, 100% { transform: translate3d(0,0,0) scale(1.05);        opacity: 0.5; }
        40%      { transform: translate3d(-70px,45px,0) scale(0.88); opacity: 0.8; }
        75%      { transform: translate3d(35px,-30px,0) scale(1.2);  opacity: 0.62; }
    }
    @keyframes tfAuroraC {
        0%, 100% { transform: translate3d(0,0,0) scale(0.95);        opacity: 0.6; }
        50%      { transform: translate3d(50px,55px,0) scale(1.25);  opacity: 0.85; }
    }
    @keyframes tfRingSpin    { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
    @keyframes tfRingSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    @keyframes tfNodeRise {
        0%   { transform: translate3d(0,0,0) scale(0.6);        opacity: 0; }
        12%  { opacity: 0.8; }
        88%  { opacity: 0.4; }
        100% { transform: translate3d(26px,-104vh,0) scale(1.1); opacity: 0; }
    }
    @keyframes tfScanSweep {
        0%   { transform: translate3d(0,-14vh,0); opacity: 0; }
        8%   { opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translate3d(0,114vh,0); opacity: 0; }
    }
    @keyframes tfSignalWave {
        0%   { transform: scale(0.6); opacity: 0.8; }
        70%  { opacity: 0.12; }
        100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes tfBreathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.06); }
    }
    @keyframes tfSheen {
        0%   { transform: translateX(-130%) skewX(-18deg); }
        100% { transform: translateX(320%)  skewX(-18deg); }
    }
    @keyframes tfFloatIcon {
        0%, 100% { transform: translate3d(0,0,0) rotate(0deg);     opacity: 0.09; }
        50%      { transform: translate3d(0,-16px,0) rotate(5deg); opacity: 0.18; }
    }
    @keyframes tfGrain {
        0%   { transform: translate3d(0,0,0); }
        20%  { transform: translate3d(-2%,2%,0); }
        40%  { transform: translate3d(2%,-1%,0); }
        60%  { transform: translate3d(-1%,-2%,0); }
        80%  { transform: translate3d(1%,1%,0); }
        100% { transform: translate3d(0,0,0); }
    }
    @keyframes tfEdgeShift {
        0%   { background-position: 0% 0%; }
        100% { background-position: 300% 0%; }
    }
    @keyframes tfSpin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;

const FONT_HREF =
    'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700' +
    '&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';

let assetsInjected = false;
const injectAssets = () => {
    if (assetsInjected || typeof document === 'undefined') return;
    assetsInjected = true;

    const link = (rel, href, cross) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = href;
        if (cross) el.crossOrigin = 'anonymous';
        document.head.appendChild(el);
    };
    link('preconnect', 'https://fonts.googleapis.com');
    link('preconnect', 'https://fonts.gstatic.com', true);
    link('stylesheet', FONT_HREF);

    const style = document.createElement('style');
    style.setAttribute('data-tf', 'register');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
};

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================
// TOASTS
// ============================================

const toastConfig = {
    success: {
        style: {
            background: alpha(palette.teal, 0.92),
            backdropFilter: 'blur(14px) saturate(180%)',
            WebkitBackdropFilter: 'blur(14px) saturate(180%)',
            color: '#fff',
            borderRadius: '14px',
            fontWeight: 600,
            padding: '12px 20px',
            border: '1px solid rgba(255,255,255,0.35)',
            boxShadow: `0 14px 34px ${alpha(palette.teal, 0.34)}, inset 0 1px 0 rgba(255,255,255,0.5)`,
        },
        iconTheme: { primary: '#fff', secondary: palette.teal },
    },
    error: {
        style: {
            background: alpha(palette.danger, 0.92),
            backdropFilter: 'blur(14px) saturate(180%)',
            WebkitBackdropFilter: 'blur(14px) saturate(180%)',
            color: '#fff',
            borderRadius: '14px',
            fontWeight: 600,
            padding: '12px 20px',
            border: '1px solid rgba(255,255,255,0.35)',
            boxShadow: `0 14px 34px ${alpha(palette.danger, 0.34)}, inset 0 1px 0 rgba(255,255,255,0.5)`,
        },
        iconTheme: { primary: '#fff', secondary: palette.danger },
    },
    info: {
        style: {
            background: alpha(palette.paper, 0.82),
            backdropFilter: 'blur(16px) saturate(180%)',
            WebkitBackdropFilter: 'blur(16px) saturate(180%)',
            color: palette.rust,
            borderRadius: '14px',
            fontWeight: 600,
            border: `1px solid ${alpha(palette.rust, 0.3)}`,
            padding: '12px 20px',
            boxShadow: `0 14px 34px rgba(${SHADOW}, 0.14), inset 0 1px 0 rgba(255,255,255,0.85)`,
        },
        iconTheme: { primary: palette.rust, secondary: palette.paper },
    },
};

// ============================================
// DATA
// ============================================

const companyTypes = [
    'Operator', 'Aggregator', 'Platform Provider', 'Infrastructure',
    'Consultancy', 'Training', 'HR Services', 'Marketing Agency', 'Other'
];

const businessTitles = [
    'Assistant', 'Sales Person', 'Manager', 'Director / VP', 'CEO'
];

const specializations = [
    'Routing', 'Product', 'Finance', 'Marketing', 'Sales', 'IT', 'Procurement', 'Roaming'
];

const regions = [
    'Europe', 'Africa', 'Asia', 'Middle East', 'USA/Canada', 'LATAM', 'Americas', 'Australia'
];

const services = [
    'Voice', 'SMS', 'Platform Provider', 'IT', 'Data', 'Training/HR', 'VAS', 'Marketing', 'Other'
];

const countries = [
    { code: 'AF', name: 'Afghanistan', dialCode: '+93', flag: '🇦🇫' },
    { code: 'AL', name: 'Albania', dialCode: '+355', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
    { code: 'AD', name: 'Andorra', dialCode: '+376', flag: '🇦🇩' },
    { code: 'AO', name: 'Angola', dialCode: '+244', flag: '🇦🇴' },
    { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenia', dialCode: '+374', flag: '🇦🇲' },
    { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
    { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijan', dialCode: '+994', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamas', dialCode: '+1-242', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbados', dialCode: '+1-246', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarus', dialCode: '+375', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belize', dialCode: '+501', flag: '🇧🇿' },
    { code: 'BJ', name: 'Benin', dialCode: '+229', flag: '🇧🇯' },
    { code: 'BT', name: 'Bhutan', dialCode: '+975', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
    { code: 'BA', name: 'Bosnia', dialCode: '+387', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswana', dialCode: '+267', flag: '🇧🇼' },
    { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
    { code: 'BN', name: 'Brunei', dialCode: '+673', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgaria', dialCode: '+359', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkina Faso', dialCode: '+226', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundi', dialCode: '+257', flag: '🇧🇮' },
    { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
    { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
    { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
    { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
    { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
    { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
    { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
    { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
    { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
    { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
];

const SELECT_COLORS = ['#d84e17', '#1f6b73', '#b8842a', '#2d7d46', '#6A1B9A', '#1a73e8', '#c2185b', '#0277bd', '#f57c00', '#00695c'];

// ============================================================================
// AURORA BACKDROP (simplified)
// ============================================================================

const AuroraBackdrop = memo(() => {
    const rootRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined' || prefersReducedMotion()) return undefined;
        const el = rootRef.current;
        if (!el) return undefined;

        let ticking = false;
        let mx = 0;
        let my = 0;

        const apply = () => {
            ticking = false;
            el.style.setProperty('--mx', mx.toFixed(4));
            el.style.setProperty('--my', my.toFixed(4));
        };

        const onMove = (e) => {
            mx = e.clientX / window.innerWidth - 0.5;
            my = e.clientY / window.innerHeight - 0.5;
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(apply);
            }
        };

        window.addEventListener('mousemove', onMove, { passive: true });
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    return (
        <Box
            ref={rootRef}
            aria-hidden="true"
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
                bgcolor: palette.canvas,
                '--mx': 0,
                '--my': 0,
            }}
        >
            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    radial-gradient(780px circle at 4% -8%,   ${alpha(palette.rust, 0.14)}, transparent 60%),
                    radial-gradient(660px circle at 96% 10%,  ${alpha(palette.teal, 0.12)}, transparent 58%),
                    radial-gradient(700px circle at 50% 110%, ${alpha(palette.gold, 0.1)}, transparent 60%),
                    radial-gradient(540px circle at 78% 66%,  ${alpha(palette.purple, 0.08)}, transparent 58%)
                `,
            }} />
            <Box
                component="svg"



             sx={[{ width: '100%', height: '100%' }, {
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.12,
                    transform: 'translate3d(calc(var(--mx) * -14px), calc(var(--my) * -14px), 0)',
                    transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
                    maskImage: 'radial-gradient(ellipse 92% 76% at 50% 42%, #000 28%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 92% 76% at 50% 42%, #000 28%, transparent 78%)',
                }]}>
                <defs>
                    <pattern id="tfGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 60" fill="none" stroke={palette.rust} strokeWidth="0.5" />
                        <circle cx="0" cy="0" r="1.5" fill={palette.teal} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#tfGrid)" />
            </Box>
        </Box>
    );
});
AuroraBackdrop.displayName = 'AuroraBackdrop';

// ============================================================================
// SELECTION GROUPS
// ============================================================================

const SelectPill = ({ label, color, selected, onClick }) => (
    <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.96 }}>
        <Box
            onClick={onClick}
            sx={{
                ...glassPill(color, selected),
                px: 2,
                py: 0.75,
                fontSize: '0.78rem',
                cursor: 'pointer',
                transition: `all 0.3s ${EASE}`,
                ...(selected && {
                    boxShadow: `0 8px 18px ${alpha(color, 0.3)}, inset 0 1px 0 rgba(255,255,255,0.6)`,
                }),
                '&:hover': {
                    borderColor: alpha(color, selected ? 0.6 : 0.4),
                    boxShadow: selected
                        ? `0 10px 22px ${alpha(color, 0.36)}`
                        : `0 6px 14px ${alpha(color, 0.15)}`,
                },
            }}
        >
            {label}
            {selected && <CheckIcon sx={{ fontSize: 14 }} />}
        </Box>
    </motion.div>
);

const AnimatedSelectionGroup = ({ label, icon, options, value, onChange, name, disabled = false, emptyMessage = "No options available" }) => {
    if (disabled || !options || options.length === 0) {
        return (
            <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.75 }}>
                    {icon} <Box component="span">{label}</Box>
                </Box>
                <Box sx={{ ...glassWell('14px'), p: 1.5, textAlign: 'center', border: `1px dashed ${alpha(palette.ink, 0.12)}` }}>
                    <Typography component="div" sx={{ color: palette.inkFaint, fontSize: '0.78rem' }}>{emptyMessage}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.75 }}>
                {icon} <Box component="span">{label}</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {options.map((opt, i) => {
                    const optLabel = typeof opt === 'object' ? opt.label : opt;
                    const optValue = typeof opt === 'object' ? opt.value : opt;
                    const isSelected = value === optValue;
                    const color = SELECT_COLORS[i % SELECT_COLORS.length];

                    return (
                        <SelectPill
                            key={optValue}
                            label={optLabel}
                            color={color}
                            selected={isSelected}
                            onClick={() => onChange({ target: { name, value: optValue } })}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

const MultiSelectAnimatedGroup = ({ label, icon, options, value, onChange, name, disabled = false, emptyMessage = "No options available" }) => {
    if (disabled || !options || options.length === 0) {
        return (
            <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.75 }}>
                    {icon} <Box component="span">{label}</Box>
                </Box>
                <Box sx={{ ...glassWell('14px'), p: 1.5, textAlign: 'center', border: `1px dashed ${alpha(palette.ink, 0.12)}` }}>
                    <Typography component="div" sx={{ color: palette.inkFaint, fontSize: '0.78rem' }}>{emptyMessage}</Typography>
                </Box>
            </Box>
        );
    }

    const selectedValues = Array.isArray(value) ? value : [];

    return (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.75 }}>
                {icon} <Box component="span">{label}</Box>
                <Box sx={{ ...glassPill(palette.gold), px: 0.75, height: 18, fontSize: '0.5rem' }}>Multi</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {options.map((opt, i) => {
                    const optLabel = typeof opt === 'object' ? opt.label : opt;
                    const optValue = typeof opt === 'object' ? opt.value : opt;
                    const isSelected = selectedValues.includes(optValue);
                    const color = SELECT_COLORS[i % SELECT_COLORS.length];

                    return (
                        <SelectPill
                            key={optValue}
                            label={optLabel}
                            color={color}
                            selected={isSelected}
                            onClick={() => {
                                let newValue;
                                if (isSelected) {
                                    newValue = selectedValues.filter(v => v !== optValue);
                                } else {
                                    newValue = [...selectedValues, optValue];
                                }
                                onChange({ target: { name, value: newValue } });
                            }}
                        />
                    );
                })}
            </Box>
            {selectedValues.length > 0 && (
                <Typography component="div" sx={{ fontSize: '0.6rem', color: palette.inkFaint, mt: 0.5 }}>
                    {selectedValues.length} selected
                </Typography>
            )}
        </Box>
    );
};

// ============================================================================
// COUNTRY SELECTOR
// ============================================================================

const popoverPaperSx = (width) => ({
    ...glassCard({ level: 4, radius: '18px' }),
    width,
    maxHeight: 400,
    overflow: 'hidden',
    mt: 1,
});

const searchFieldSx = {
    '& .MuiOutlinedInput-root': {
        ...glassWell('10px'),
        '& fieldset': { border: 'none' },
        '& input': { fontSize: '0.8rem', py: 0.6, color: palette.ink },
    },
};

const countryItemSx = {
    py: 0.75,
    px: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    borderBottom: `1px solid ${alpha(palette.ink, 0.04)}`,
    transition: 'all 0.2s ease',
    '&:hover': { bgcolor: alpha(palette.rust, 0.06) },
    '&.Mui-selected': { bgcolor: alpha(palette.rust, 0.08) },
};

const PhoneCountrySelector = ({ open, anchorEl, onClose, onSelect, selectedCountry, countries }) => {
    const [search, setSearch] = useState('');
    const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.dialCode.includes(search) || c.code.toLowerCase().includes(search.toLowerCase()));

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
                backdrop: { sx: { backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } },
                paper: { sx: popoverPaperSx(320) },
            }}
        >
            <Box sx={{ p: 1, borderBottom: `1px solid ${alpha(palette.ink, 0.06)}` }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={searchFieldSx}
                    slotProps={{ input: {
                        startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: palette.inkSoft }} /></InputAdornment>),
                        endAdornment: search && (<InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 12 }} /></IconButton></InputAdornment>)
                    } }}
                />
            </Box>
            <MenuList sx={{ maxHeight: 320, overflowY: 'auto', py: 0 }}>
                {filteredCountries.map((country) => (
                    <MenuItem key={country.code} onClick={() => { onSelect(country); onClose(); }} sx={countryItemSx}>
                        <Typography component="div" sx={{ fontSize: 18 }}>{country.flag}</Typography>
                        <Typography component="div" sx={{ flex: 1, fontSize: '0.8rem', fontWeight: selectedCountry?.code === country.code ? 700 : 500, color: palette.ink }}>{country.name}</Typography>
                        <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.75rem' }}>{country.dialCode}</Typography>
                        {selectedCountry?.code === country.code && <CheckCircleIcon sx={{ color: palette.rust, fontSize: 14 }} />}
                    </MenuItem>
                ))}
                {filteredCountries.length === 0 && (<Box sx={{ p: 2, textAlign: 'center' }}><Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.75rem' }}>No countries found</Typography></Box>)}
            </MenuList>
        </Popover>
    );
};

// ============================================================================
// IMAGE UPLOAD COMPONENT
// ============================================================================

const ImageUploadField = ({ label, icon, preview, onUpload, onRemove, size = 72 }) => {
    const fileInputRef = useRef(null);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                <Box
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        ...glassWell('16px'),
                        width: size,
                        height: size,
                        border: `2px dashed ${preview ? palette.teal : alpha(palette.ink, 0.15)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: preview ? `url(${preview}) center/cover no-repeat` : 'transparent',
                        cursor: 'pointer',
                        transition: `all 0.3s ${EASE}`,
                        '&:hover': {
                            borderColor: palette.teal,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 20px ${alpha(palette.teal, 0.15)}`,
                        },
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {!preview ? (
                        <>
                            {React.cloneElement(icon, { sx: { color: palette.teal, fontSize: 24, mb: 0.25 } })}
                            <Typography component="div" sx={{ color: palette.inkFaint, fontSize: '0.55rem', fontWeight: 600, textAlign: 'center' }}>
                                {label}
                            </Typography>
                        </>
                    ) : (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove && onRemove();
                            }}
                            sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                ...glassPod(palette.danger, 20, '50%'),
                                cursor: 'pointer',
                                '&:hover': { transform: 'scale(1.1)' },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 12 }} />
                        </Box>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={onUpload}
                        style={{ display: 'none' }}
                    />
                </Box>
            </motion.div>
            <Typography component="div" sx={{ fontSize: '0.6rem', color: palette.inkFaint, fontWeight: 500 }}>
                {preview ? 'Change' : 'Upload'}
            </Typography>
        </Box>
    );
};

// ============================================================================
// PAYMENT DIALOG
// ============================================================================

const SubscriptionPaymentDialog = ({ open, onClose, onSuccess, onPending, selectedPackage, currency, companyName, email, phone, userId = null, companyId = null }) => {
    const handleOnlinePayment = async () => {
        if (!selectedPackage?.id || !userId) throw new Error('Registration profile or package information is incomplete.');
        const result = await startRegistrationPayment({
            packageId: selectedPackage.id,
            customer: { name: companyName || '', email: email || '', phone: phone || '' },
            themeColor: palette.rust,
        });
        if (result?.redirected) return result;
        if (result?.activated || result?.ok || result?.provider === 'free' || result?.paymentId) {
            const status = await getRegistrationPaymentStatus();
            if (status?.active === false) throw new Error('Payment was received but activation is still pending. Please retry in a moment.');
            if (companyId && email && Number(selectedPackage?.price || 0) > 0) {
                sendConfirmedOnlineReceipt({
                    companyId,
                    email,
                    payment: {
                        id: result?.paymentId || result?.orderId || result?.sessionId || `registration_${userId}_${selectedPackage.id}`,
                        companyName,
                        userName: companyName,
                        paymentType: 'company_registration',
                        packageId: selectedPackage.id,
                        packageName: selectedPackage.name,
                        amount: Number(selectedPackage.price || 0),
                        currency: selectedPackage.currency || currency || 'EUR',
                        provider: result?.provider || 'online_gateway',
                        providerReference: result?.paymentId || result?.orderId || result?.sessionId || '',
                        status: 'approved',
                    },
                }).catch((receiptError) => console.warn('Online receipt email skipped:', receiptError?.message || receiptError));
            }
            toast.success('Payment verified and registration activated.', toastConfig.success);
            setTimeout(() => onSuccess?.(), 500);
            return { ...result, activated: true };
        }
        throw new Error('Payment could not be verified.');
    };

    return (
        <PaymentMethodDialog
            open={open}
            onClose={onClose}
            onOnlinePayment={handleOnlinePayment}
            onManualSubmitted={onPending}
            companyId={companyId}
            userId={userId}
            companyName={companyName}
            userName={companyName}
            email={email}
            paymentType="company_registration"
            plan={selectedPackage}
            currency={currency}
        />
    );
};

const ExtraSeatPaymentDialog = ({ open, payment, onClose, onSubmitted }) => {
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const amount = Number(payment?.amount || 0);
    const currency = payment?.currency || 'EUR';
    const submit = async () => {
        if (!payment?.companyId) return;
        if (!reference.trim()) {
            toast.error('Enter the payment transaction/reference number.');
            return;
        }
        setBusy(true);
        try {
            await submitExtraSeatPayment(payment.companyId, { transactionReference: reference.trim(), notes: notes.trim() });
            toast.success('Extra user-seat payment submitted for company review.');
            onSubmitted?.();
        } catch (error) {
            toast.error(friendlyError(error, 'Unable to submit the extra-seat payment.'));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={Boolean(open)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 900, color: palette.ink }}>Additional user seat required</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
                    This company has used all user seats included in its subscription. The company has set an additional-user charge for registrations above the package limit.
                </Alert>
                <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '14px', border: `1px solid ${alpha(palette.rust,.14)}`, bgcolor: alpha(palette.rust,.04) }}>
                    <Typography sx={{ fontSize: '.72rem', fontWeight: 800, color: palette.inkSoft, textTransform: 'uppercase' }}>Additional seat amount</Typography>
                    <Typography sx={{ mt: .4, fontSize: '1.65rem', fontWeight: 950, color: palette.rust }}>{currency} {amount.toFixed(2)}</Typography>
                    {payment?.instructions && <Typography sx={{ mt: 1, fontSize: '.82rem', color: palette.inkSoft, whiteSpace: 'pre-wrap' }}>{payment.instructions}</Typography>}
                    {payment?.paymentLink && <Button component="a" href={safeExternalUrl(payment.paymentLink, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon />} sx={{ mt: 1, textTransform: 'none', fontWeight: 800 }}>Open company payment link</Button>}
                </Paper>
                <TextField fullWidth label="Transaction / payment reference" value={reference} onChange={(e) => setReference(e.target.value)} margin="dense" />
                <TextField fullWidth label="Payment note (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} margin="dense" multiline minRows={2} />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button onClick={submit} disabled={busy} variant="contained" startIcon={<PaymentIcon />} sx={{ textTransform: 'none', fontWeight: 850 }}>{busy ? 'Submitting…' : 'Submit payment reference'}</Button>
            </DialogActions>
        </Dialog>
    );
};

const SubscriptionSelection = ({ packages, selectedPackage, setSelectedPackage, currency, loading }) => {
    const currencySymbol = currency === 'EUR' ? '€' : '$';

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: palette.teal }} />
                <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.75rem', mt: 1 }}>Loading packages...</Typography>
            </Box>
        );
    }

    if (packages.length === 0) {
        return (
            <Alert severity="info" sx={{ borderRadius: '10px', bgcolor: alpha(palette.teal, 0.06), border: `1px solid ${alpha(palette.teal, 0.2)}` }}>
                <Typography variant="caption">No subscription packages available. Contact support.</Typography>
            </Alert>
        );
    }

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {packages.map((pkg, index) => {
                const isSelected = selectedPackage?.id === pkg.id;
                const colors = [palette.rust, palette.teal, palette.gold, palette.purple, palette.green];
                const color = colors[index % colors.length];
                
                return (
                    <motion.div
                        key={pkg.id}
                        whileHover={{ y: -3 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Paper
                            elevation={0}
                            onClick={() => setSelectedPackage(pkg)}
                            sx={{
                                ...glassCard({ level: isSelected ? 3 : 1, radius: '14px', accent: isSelected ? color : null }),
                                p: 2,
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: `all 0.3s ${EASE}`,
                                border: isSelected ? `2px solid ${color}` : `1px solid ${alpha(palette.ink, 0.06)}`,
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    ...glassLift(2, color),
                                },
                            }}
                        >
                            {pkg.isPopular && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: -6,
                                    right: -6,
                                    ...glassPill(palette.gold, true),
                                    px: 1,
                                    height: 18,
                                    fontSize: '0.5rem'
                                }}>
                                    ⭐ Popular
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                                <Box sx={glassPod(color, 32, '10px')}>
                                    {index === 0 ? <StarIcon sx={{ fontSize: 16 }} /> :
                                     index === 1 ? <RocketIcon sx={{ fontSize: 16 }} /> :
                                     index === 2 ? <BusinessIcon sx={{ fontSize: 16 }} /> :
                                     <PackageIcon sx={{ fontSize: 16 }} />}
                                </Box>
                            </Box>
                            <Typography component="div" sx={{ fontWeight: 700, fontSize: '0.82rem', color: palette.ink }}>
                                {pkg.name}
                            </Typography>
                            <Typography component="div" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem', color }}>
                                {currencySymbol}{pkg.price}
                            </Typography>
                            <Typography component="div" sx={{ fontSize: '0.55rem', color: palette.inkFaint, textTransform: 'capitalize' }}>
                                {pkg.billingCycle || 'One-time'}
                            </Typography>
                            <Box sx={{ ...glassPill(color, false), mt: .7, mx: 'auto', width: 'fit-content', fontSize: '.55rem' }}>
                                <PeopleIcon sx={{ fontSize: 12 }} />
                                {pkg.unlimitedUsers ? 'Unlimited users' : `${pkg.userLimit ?? pkg.maxUsers ?? 1} user seats`}
                            </Box>
                            {isSelected && (
                                <CheckIcon sx={{ color, fontSize: 14, mt: 0.5 }} />
                            )}
                            {pkg.description && (
                                <Typography component="div" sx={{ fontSize: '0.6rem', color: palette.inkSoft, mt: 0.5 }}>
                                    {pkg.description}
                                </Typography>
                            )}
                        </Paper>
                    </motion.div>
                );
            })}
        </Box>
    );
};

// ============================================================================
// MAIN REGISTER COMPONENT
// ============================================================================

function Register() {
    const navigate = useNavigate();
    const { login, verifyAccountWithWhatsAppOtp } = useAuth();

    injectAssets();

    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [verificationCheckCount, setVerificationCheckCount] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [registrationMode, setRegistrationMode] = useState('join');
    const [createdUser, setCreatedUser] = useState(null);
    const [verificationInterval, setVerificationInterval] = useState(null);
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentCompleted, setPaymentCompleted] = useState(false);
    const [showSubscriptionStep, setShowSubscriptionStep] = useState(false);
    const [isPaymentRequired, setIsPaymentRequired] = useState(false);
    const [verificationProvider, setVerificationProvider] = useState('firebase_auth');
    const [registrationWhatsAppOtp, setRegistrationWhatsAppOtp] = useState('');
    const [registrationData, setRegistrationData] = useState(null);
    const [extraSeatPayment, setExtraSeatPayment] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resumeProgress, setResumeProgress] = useState(null);
    const [restoringProgress, setRestoringProgress] = useState(false);
    const [progressSavedAt, setProgressSavedAt] = useState(null);
    const progressSaveTimer = useRef(null);

    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    const [subscriptionSettings, setSubscriptionSettings] = useState({ 
        paymentRequired: false, 
        currency: 'EUR', 
        defaultPackageId: null,
    });
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [passwordPolicy, setPasswordPolicy] = useState({
        minLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: true,
    });
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [loadingPackages, setLoadingPackages] = useState(false);
    const [paymentGateways, setPaymentGateways] = useState([]);

    const [showCertificate, setShowCertificate] = useState(false);
    const [registeredData, setRegisteredData] = useState(null);
    const registrationCertificateRef = useRef(null);
    const [certificateDownloading, setCertificateDownloading] = useState(false);

    // Image upload states
    const [profileImagePreview, setProfileImagePreview] = useState(null);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
    const [companyLogoFile, setCompanyLogoFile] = useState(null);
    const [companyCoverPreview, setCompanyCoverPreview] = useState(null);
    const [companyCoverFile, setCompanyCoverFile] = useState(null);

    // NEW: LinkedIn Profile state
    const [linkedinError, setLinkedinError] = useState('');

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        title: '',
        country: null,
        companyName: '',
        companyWebsite: '',
        companyType: '',
        businessTitle: '',
        specialization: [],
        primaryRegionAvailability: [],
        primaryServiceProvided: [],
        secondaryRegionAvailability: [],
        secondaryServiceProvided: [],
        preferredRegion: [],
        primaryServiceBuying: [],
        secondaryPreferredRegion: [],
        secondaryServiceBuying: [],
        phone: '',
        dialCode: '+1',
        countryCode: 'US',
        linkedinProfile: '', // NEW: LinkedIn Profile field
    });

    const [countrySearch, setCountrySearch] = useState('');
    const [countrySearchAnchorEl, setCountrySearchAnchorEl] = useState(null);
    const [phoneCountryAnchorEl, setPhoneCountryAnchorEl] = useState(null);

    const steps = ['Create Account', 'Complete Profile', 'Your Interests'];

    const buildRegistrationDraft = () => ({
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        country: formData.country,
        companyName: formData.companyName,
        companyWebsite: formData.companyWebsite,
        companyType: formData.companyType,
        businessTitle: formData.businessTitle,
        specialization: formData.specialization,
        primaryRegionAvailability: formData.primaryRegionAvailability,
        primaryServiceProvided: formData.primaryServiceProvided,
        secondaryRegionAvailability: formData.secondaryRegionAvailability,
        secondaryServiceProvided: formData.secondaryServiceProvided,
        preferredRegion: formData.preferredRegion,
        primaryServiceBuying: formData.primaryServiceBuying,
        secondaryPreferredRegion: formData.secondaryPreferredRegion,
        secondaryServiceBuying: formData.secondaryServiceBuying,
        phone: formData.phone,
        dialCode: formData.dialCode,
        countryCode: formData.countryCode,
        linkedinProfile: formData.linkedinProfile,
        registrationMode,
        selectedCompanyId: selectedCompany?.id || '',
        selectedPackageId: selectedPackage?.id || '',
    });

    const persistRegistrationStage = async (stage, { silent = true } = {}) => {
        if (!auth.currentUser) return null;
        try {
            const result = await saveRegistrationProgress({
                stage,
                draft: buildRegistrationDraft(),
                origin: window.location.origin,
            });
            setResumeProgress((previous) => ({ ...(previous || {}), ...(result || {}), stage: result?.stage || stage }));
            setProgressSavedAt(Date.now());
            return result;
        } catch (progressError) {
            console.error('Registration progress save failed:', progressError);
            if (!silent) setError(friendlyError(progressError, 'Unable to save registration progress.'));
            return null;
        }
    };

    // LinkedIn URL validation helper
    const isValidLinkedInUrl = (url) => {
        if (!url || url.trim() === '') return true; // Optional field
        const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company|school)\/[a-zA-Z0-9-]+\/?$/;
        return linkedinRegex.test(url.trim());
    };

    // Fetch data on mount
    useEffect(() => {
        fetchRegistrationSettings();
        fetchPackages();
        fetchPaymentGateways();
        fetchCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup verification interval
    useEffect(() => {
        return () => {
            if (verificationInterval) {
                clearInterval(verificationInterval);
            }
        };
    }, [verificationInterval]);



    // Restore an incomplete account after refresh/login and return the user to
    // the exact registration stage instead of making them start again.
    useEffect(() => {
        let cancelled = false;
        const restore = async () => {
            const signedIn = auth.currentUser;
            if (!signedIn) return;
            setRestoringProgress(true);
            try {
                await signedIn.reload();
                setCreatedUser(signedIn);

                const progress = await getRegistrationProgress();
                if (cancelled || !progress?.exists) return;
                const savedStatus = String(progress.registrationStatus || '').toLowerCase();
                const savedStage = String(progress.registrationStage || progress.stage || '').toLowerCase();
                if (savedStatus === 'expired' || savedStage === 'expired') {
                    setError('This incomplete registration has expired. Contact TELCOFAIR support or ask Super Admin to reactivate it.');
                    await signOut(auth).catch(() => {});
                    navigate('/login', { replace: true });
                    return;
                }
                if (savedStatus === 'declined' || savedStage === 'declined') {
                    setError('This registration was declined. Contact the company administrator or TELCOFAIR support before trying again.');
                    await signOut(auth).catch(() => {});
                    navigate('/login', { replace: true });
                    return;
                }
                let restoreProvider = 'firebase_auth';
                try {
                    const runtime = await getPublicPlatformRuntime();
                    restoreProvider = runtime?.auth?.verificationProvider || 'firebase_auth';
                    setVerificationProvider(restoreProvider);
                } catch (_) { /* Firebase Auth remains the safe default. */ }
                const restoredVerified = restoreProvider === 'firebase_auth'
                    ? Boolean(signedIn.emailVerified)
                    : restoreProvider === 'whatsapp'
                        ? Boolean(progress.whatsappVerified || progress.verificationOverride)
                        : restoreProvider === 'manual'
                            ? Boolean(progress.verificationOverride || progress.isVerified)
                            : Boolean(progress.emailVerified || progress.verificationOverride || progress.isVerified);
                const deliveryStatus = String(progress.verificationDeliveryStatus || '').toLowerCase();
                setVerificationSent(restoredVerified || ['sent', 'already_verified'].includes(deliveryStatus));
                setIsEmailVerified(restoredVerified);
                setResumeProgress(progress);

                const draft = progress.draft || {};
                if (Object.keys(draft).length) {
                    setFormData((previous) => ({
                        ...previous,
                        ...draft,
                        email: signedIn.email || previous.email,
                        password: previous.password,
                        confirmPassword: previous.confirmPassword,
                        country: draft.country?.name ? draft.country : previous.country,
                    }));
                    if (draft.registrationMode === 'create') setRegistrationMode('create');
                    else if (draft.registrationMode === 'join') setRegistrationMode('join');
                }

                if (progress.stage === 'completed' && progress.isActive) {
                    navigate('/dashboard', { replace: true });
                    return;
                }
                if (progress.stage === 'business_interests' || progress.stage === 'company_subscription' || progress.profileCompleted) setActiveStep(2);
                else if (progress.stage === 'profile_completion' || restoredVerified) setActiveStep(1);
                else setActiveStep(0);
            } catch (restoreError) {
                console.warn('Incomplete registration restore skipped:', restoreError?.message || restoreError);
            } finally {
                if (!cancelled) setRestoringProgress(false);
            }
        };
        restore();
        return () => { cancelled = true; };
    }, [navigate]);

    // Save the in-progress profile after the user pauses typing. Passwords and
    // raw image files are intentionally excluded from the saved draft.
    useEffect(() => {
        if (!auth.currentUser || activeStep === 0 || restoringProgress) return undefined;
        const savedStatus = String(resumeProgress?.registrationStatus || '').toLowerCase();
        const savedStage = String(resumeProgress?.registrationStage || resumeProgress?.stage || '').toLowerCase();
        const protectedPendingStage = [
            'pending_company_subscription', 'company_payment_pending', 'company_payment_pending_review', 'pending_payment', 'pending_verification', 'company_payment_rejected',
            'pending_extra_seat_payment', 'pending_extra_seat_payment_review', 'pending_company_approval',
        ].includes(savedStatus) || ['company_subscription', 'extra_seat_payment', 'company_approval'].includes(savedStage);
        if (protectedPendingStage) return undefined;
        if (progressSaveTimer.current) clearTimeout(progressSaveTimer.current);
        progressSaveTimer.current = setTimeout(() => {
            const stage = activeStep >= 2 ? 'business_interests' : 'profile_completion';
            persistRegistrationStage(stage, { silent: true });
        }, 2200);
        return () => {
            if (progressSaveTimer.current) clearTimeout(progressSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, registrationMode, selectedCompany?.id, selectedPackage?.id, activeStep, restoringProgress, resumeProgress?.registrationStatus, resumeProgress?.registrationStage, resumeProgress?.stage]);

    useEffect(() => {
        if (!packages.length) return;
        const savedPackageId = resumeProgress?.registrationPackageId || resumeProgress?.draft?.selectedPackageId || subscriptionSettings.defaultPackageId;
        setSelectedPackage((previous) => {
            if (previous && packages.some((pkg) => pkg.id === previous.id)) return previous;
            const saved = savedPackageId ? packages.find((pkg) => pkg.id === savedPackageId) : null;
            return saved || packages.find((pkg) => Number(pkg.price || 0) === 0) || packages[0];
        });
    }, [packages, resumeProgress?.registrationPackageId, resumeProgress?.draft?.selectedPackageId, subscriptionSettings.defaultPackageId]);

    useEffect(() => {
        const companyId = resumeProgress?.draft?.selectedCompanyId;
        if (!companyId || !companies.length || selectedCompany) return;
        const match = companies.find((company) => (company.id || company.companyId) === companyId);
        if (match) setSelectedCompany(match);
    }, [companies, resumeProgress, selectedCompany]);

    // Stripe returns to this page after its verified webhook. Poll the secure
    // backend activation status instead of trusting query-string success alone.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const paymentResult = params.get('registration_payment');
        if (!paymentResult) return undefined;
        let cancelled = false;

        if (paymentResult === 'cancelled') {
            setError('Payment was cancelled. Your profile is saved and remains inactive until payment is completed.');
            window.history.replaceState({}, document.title, window.location.pathname);
            return undefined;
        }

        if (paymentResult === 'success') {
            setIsLoading(true);
            const check = async () => {
                for (let attempt = 0; attempt < 10 && !cancelled; attempt += 1) {
                    try {
                        const status = await getRegistrationPaymentStatus();
                        if (status?.active) {
                            reconcileApprovedOnlineRegistrationReceipt().catch((receiptError) => console.warn('Online receipt reconciliation skipped:', receiptError?.message || receiptError));
                            toast.success('Payment verified. Your TELCOFAIR account is active.', toastConfig.success);
                            window.history.replaceState({}, document.title, window.location.pathname);
                            navigate('/dashboard', { replace: true });
                            return;
                        }
                    } catch (_) { /* webhook may still be finalizing */ }
                    await new Promise(resolve => setTimeout(resolve, 1500));
                }
                if (!cancelled) setError('Payment was received, but activation is still being confirmed. Refresh shortly; you will not be charged again.');
                setIsLoading(false);
            };
            check();
        }
        return () => { cancelled = true; };
    }, [navigate]);

    useEffect(() => {
        const companyPurchase = registrationMode === 'create';
        setIsPaymentRequired(companyPurchase && Boolean(subscriptionSettings.paymentRequired));
        setShowSubscriptionStep(companyPurchase);
    }, [registrationMode, subscriptionSettings.paymentRequired]);

    const fetchRegistrationSettings = async () => {
        setLoadingSettings(true);
        try {
            let runtime = null;
            try { runtime = await getPublicPlatformRuntime(); } catch (_) { /* Firestore fallback below */ }
            const runtimePayment = runtime?.registration?.paymentRequired;
            const runtimeVerification = runtime?.auth?.verificationProvider;
            const runtimePasswordPolicy = runtime?.security?.passwordPolicy || {};
            setPasswordPolicy({
                minLength: Math.max(8, Math.min(128, Number(runtimePasswordPolicy.minLength || 8))),
                requireUppercase: runtimePasswordPolicy.requireUppercase !== false,
                requireLowercase: runtimePasswordPolicy.requireLowercase !== false,
                requireNumbers: runtimePasswordPolicy.requireNumbers !== false,
                requireSpecialChars: runtimePasswordPolicy.requireSpecialChars !== false,
            });
            if (runtimeVerification) setVerificationProvider(runtimeVerification);

            const settingsDoc = await getDoc(doc(db, 'systemConfig', 'platform'));
            if (settingsDoc.exists()) {
                const data = settingsDoc.data();
                const subscriptionData = data?.value?.subscription || data?.subscription || {};
                const generalData = data?.value?.general || data?.general || {};
                const registrationIntegrations = data?.registrationIntegrations || {};
                setVerificationProvider(runtimeVerification || registrationIntegrations.verificationProvider || 'firebase_auth');
                setSubscriptionSettings({
                    paymentRequired: runtimePayment ?? registrationIntegrations.paymentRequired ?? registrationIntegrations.paymentEnabled ?? Boolean(subscriptionData.status === 'active'),
                    currency: runtime?.payments?.currency || generalData.currency || 'EUR',
                    defaultPackageId: subscriptionData.plan || subscriptionData.defaultPackageId || null,
                });
            } else {
                setSubscriptionSettings({ paymentRequired: Boolean(runtimePayment), currency: runtime?.payments?.currency || 'EUR', defaultPackageId: null });
            }
        } catch (loadError) {
            console.error('Registration settings load failed:', loadError);
            setSubscriptionSettings({ paymentRequired: false, currency: 'EUR', defaultPackageId: null });
            setVerificationProvider('firebase_auth');
        } finally {
            setLoadingSettings(false);
        }
    };

    const fetchPackages = async () => {
        setLoadingPackages(true);
        try {
            const packagesRef = collection(db, 'subscriptionPlans');
            const q = query(packagesRef, where('isActive', '==', true));
            const snapshot = await getDocs(q);
            const packagesList = [];
            snapshot.forEach((docSnap) => { 
                const data = docSnap.data();
                if (data.name && data.price !== undefined) {
                    packagesList.push({ id: docSnap.id, ...data }); 
                }
            });
            packagesList.sort((a, b) => (a.price || 0) - (b.price || 0));
            setPackages(packagesList);

        } catch (error) {
            console.error('Error fetching packages:', error);
        } finally {
            setLoadingPackages(false);
        }
    };

    const fetchPaymentGateways = async () => {
        try {
            const gatewaysRef = collection(db, 'paymentGateways');
            const q = query(gatewaysRef);
            const snapshot = await getDocs(q);
            const gateways = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const isActive = data.status === 'active' || data.isActive === true || data.status === 'live';
                if (isActive) {
                    gateways.push({ 
                        id: docSnap.id, 
                        ...data, 
                        name: data.name || data.provider || 'Payment Gateway',
                        status: 'active'
                    });
                }
            });
            setPaymentGateways(gateways);
        } catch (error) {
            console.error('Error fetching payment gateways:', error);
            setPaymentGateways([]);
        }
    };

    const fetchCompanies = async () => {
        setLoadingCompanies(true);
        try {
            const companiesRef = collection(db, 'companies');
            const querySnapshot = await getDocs(companiesRef);
            const companiesList = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (docSnap.id !== '_schema' && docSnap.id !== '_placeholder' && docSnap.id !== 'system') {
                    const subscription = data.subscription || {};
                    const status = String(subscription.status || data.subscriptionStatus || '').toLowerCase();
                    const expiryValue = subscription.expiryDate || subscription.expiresAt;
                    const expiry = expiryValue?.toDate ? expiryValue.toDate() : expiryValue?.seconds ? new Date(expiryValue.seconds * 1000) : expiryValue ? new Date(expiryValue) : null;
                    const expired = expiry instanceof Date && !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
                    const blockedStatus = ['expired', 'cancelled', 'canceled', 'suspended', 'pending_payment', 'inactive'].includes(status);
                    if (data.isBlocked !== true && data.isActive !== false && !expired && !blockedStatus) {
                        companiesList.push({ id: docSnap.id, ...data });
                    }
                }
            });
            setCompanies(companiesList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        } finally {
            setLoadingCompanies(false);
        }
    };

    // Check if company exists by URL
    const checkCompanyExistsByUrl = async (website) => {
        try {
            let normalizedUrl = website.trim();
            if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
                normalizedUrl = 'https://' + normalizedUrl;
            }
            normalizedUrl = normalizedUrl.replace(/\/+$/, '');

            const companiesRef = collection(db, 'companies');
            const q = query(companiesRef, where('website', '==', normalizedUrl));
            const querySnapshot = await getDocs(q);
            return { exists: !querySnapshot.empty, url: normalizedUrl };
        } catch (error) {
            console.error('Error checking company:', error);
            throw new Error(friendlyError(error, 'Unable to validate the company website right now. Please try again.'));
        }
    };

    const checkCompanyExistsByName = async (companyName) => {
        try {
            const companiesRef = collection(db, 'companies');
            const q = query(companiesRef, where('companyName', '==', companyName));
            const querySnapshot = await getDocs(q);
            return !querySnapshot.empty;
        } catch (error) {
            console.error('Error checking company:', error);
            return false;
        }
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // Validate LinkedIn URL on change
        if (name === 'linkedinProfile') {
            if (value && !isValidLinkedInUrl(value)) {
                setLinkedinError('Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)');
            } else {
                setLinkedinError('');
            }
        }
    };

    const handleSelectCountry = (country) => {
        setFormData({ ...formData, country });
        setCountrySearch('');
        setCountrySearchAnchorEl(null);
    };

    const handleSelectPhoneCountry = (country) => {
        setFormData({ ...formData, dialCode: country.dialCode, countryCode: country.code });
        setPhoneCountryAnchorEl(null);
    };

    // Image upload handlers. Registration media is restricted to formats that
    // browsers can render safely and consistently across the platform. SVG is
    // intentionally excluded because it can contain active markup.
    const validateRegistrationImage = (file, label = 'Image') => {
        if (!file) return false;
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            setError(`${label} must be a PNG, JPG or WEBP image.`);
            return false;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError(`${label} must be smaller than 5 MB.`);
            return false;
        }
        setError('');
        return true;
    };

    const handleProfileImageUpload = (e) => {
        const file = e.target.files[0];
        if (!validateRegistrationImage(file, 'Profile photo')) { if (e?.target) e.target.value = ''; return; }
        const reader = new FileReader();
        reader.onloadend = () => setProfileImagePreview(reader.result);
        reader.readAsDataURL(file);
        setProfileImageFile(file);
    };

    const handleProfileImageRemove = () => {
        setProfileImagePreview(null);
        setProfileImageFile(null);
    };

    const handleCompanyLogoUpload = (e) => {
        const file = e.target.files[0];
        if (!validateRegistrationImage(file, 'Company logo')) { if (e?.target) e.target.value = ''; return; }
        const reader = new FileReader();
        reader.onloadend = () => setCompanyLogoPreview(reader.result);
        reader.readAsDataURL(file);
        setCompanyLogoFile(file);
    };

    const handleCompanyLogoRemove = () => {
        setCompanyLogoPreview(null);
        setCompanyLogoFile(null);
    };

    const handleCompanyCoverUpload = (e) => {
        const file = e.target.files[0];
        if (!validateRegistrationImage(file, 'Company cover')) { if (e?.target) e.target.value = ''; return; }
        const reader = new FileReader();
        reader.onloadend = () => setCompanyCoverPreview(reader.result);
        reader.readAsDataURL(file);
        setCompanyCoverFile(file);
    };

    const handleCompanyCoverRemove = () => {
        setCompanyCoverPreview(null);
        setCompanyCoverFile(null);
    };

    // Registration uploads use the same platform-owned storage provider as the
    // rest of TELCOFAIR. Before a company exists these assets are scoped to the
    // authenticated user; the resulting URLs are then copied into the created
    // company/profile records.
    const uploadImage = async (file, path) => {
        validateUploadFile(file, { kind: 'image', maxBytes: 5 * 1024 * 1024 });
        const parts = String(path || 'registration').split('/').filter(Boolean);
        const folder = ['registration', ...parts.slice(0, -1)].join('/');
        try {
            const asset = await uploadFile({
                scope: 'user',
                userId: auth.currentUser?.uid,
                ownerId: auth.currentUser?.uid,
                file,
                kind: 'image',
                maxBytes: 5 * 1024 * 1024,
                folder,
            });
            return asset.url;
        } catch (error) {
            console.error('Upload error:', error);
            throw new Error(friendlyError(error, 'Image upload failed.'));
        }
    };

    const sendConfiguredVerification = async (user) => {
        if (!user) throw new Error('Account session is unavailable. Please sign in again.');

        if (verificationProvider === 'firebase_auth') {
            // Use Firebase's native hosted verification flow by default. Passing a
            // continue URL here makes delivery depend on Firebase Authorized Domains;
            // that caused valid Auth accounts to be created while verification mail
            // was rejected on localhost/new deployment domains.
            await user.reload();
            if (user.emailVerified) {
                await setDoc(doc(db, 'users', user.uid), {
                    emailVerified: true,
                    isVerified: true,
                    verificationMethod: 'firebase_auth',
                    verificationDeliveryStatus: 'already_verified',
                    verificationDeliveryError: null,
                    updatedAt: serverTimestamp(),
                }, { merge: true });
                return { provider: 'firebase_auth', alreadyVerified: true };
            }

            try {
                await sendEmailVerification(user);
                await setDoc(doc(db, 'users', user.uid), {
                    verificationMethod: 'firebase_auth',
                    verificationDeliveryStatus: 'sent',
                    verificationDeliveryError: null,
                    verificationLastSentAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                }, { merge: true });
                return { provider: 'firebase_auth', success: true };
            } catch (error) {
                const message = friendlyError(error, 'Firebase could not send the verification email. Check Firebase Authentication email templates and Authorized Domains, then try Resend.');
                await setDoc(doc(db, 'users', user.uid), {
                    verificationMethod: 'firebase_auth',
                    verificationDeliveryStatus: 'failed',
                    verificationDeliveryError: message,
                    updatedAt: serverTimestamp(),
                }, { merge: true }).catch(() => {});
                throw error;
            }
        }

        return sendProfessionalVerificationEmail({
            uid: user.uid,
            email: String(formData.email || user.email || '').trim().toLowerCase(),
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
            origin: window.location.origin,
        });
    };

    const assertRegistrationVerified = async () => {
        const user = auth.currentUser;
        if (!user?.uid) throw new Error('Your account session has expired. Please sign in again.');

        if (verificationProvider === 'firebase_auth') {
            await user.reload();
            if (!user.emailVerified) {
                throw new Error('Verify your email address before completing registration.');
            }
            await setDoc(doc(db, 'users', user.uid), {
                emailVerified: true,
                isVerified: true,
                verificationMethod: 'firebase_auth',
                verifiedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            }, { merge: true });
            return true;
        }

        const snap = await getDoc(doc(db, 'users', user.uid));
        const profile = snap.exists() ? snap.data() || {} : {};
        const verified = verificationProvider === 'whatsapp'
            ? Boolean(profile.whatsappVerified || profile.verificationOverride)
            : verificationProvider === 'manual'
                ? Boolean(profile.verificationOverride || profile.isVerified)
                : Boolean(profile.emailVerified || profile.verificationOverride || profile.isVerified);

        if (!verified) throw new Error('Complete account verification before finishing registration.');
        return true;
    };

    // ============================================
    // STEP 1: Create Account with Email Verification
    // ============================================

    const restoreExistingAccountForRegistration = async (email, password) => {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        const user = credential.user;
        await user.reload();
        setCreatedUser(user);

        let progress = await getRegistrationProgress();
        const status = String(progress?.registrationStatus || '').toLowerCase();
        const stage = String(progress?.registrationStage || progress?.stage || '').toLowerCase();
        if (status === 'expired' || stage === 'expired') {
            await signOut(auth).catch(() => {});
            throw new Error('This incomplete registration has expired. Contact TELCOFAIR support or ask Super Admin to reactivate it.');
        }
        if (status === 'declined' || stage === 'declined') {
            await signOut(auth).catch(() => {});
            throw new Error('This registration was declined. Contact the company administrator or TELCOFAIR support before trying again.');
        }
        const completed = progress?.registrationIncomplete === false
            && progress?.profileCompleted === true
            && ['completed', 'active', 'done'].includes(stage || status);
        if (completed) {
            await signOut(auth).catch(() => {});
            throw new Error('This account is already registered. Please use Sign In instead.');
        }

        if (!progress?.exists) {
            await saveRegistrationProgress({
                stage: user.emailVerified ? 'profile_completion' : 'email_verification',
                draft: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    registrationMode,
                    phone: formData.phone,
                },
                origin: window.location.origin,
            });
            progress = await getRegistrationProgress();
        }

        const profileSnap = await getDoc(doc(db, 'users', user.uid));
        const profile = profileSnap.exists() ? profileSnap.data() || {} : {};
        const verified = verificationProvider === 'firebase_auth'
            ? Boolean(user.emailVerified)
            : verificationProvider === 'whatsapp'
                ? Boolean(profile.whatsappVerified || profile.isVerified || profile.verificationOverride)
                : verificationProvider === 'manual'
                    ? Boolean(profile.isVerified || profile.verificationOverride)
                    : Boolean(profile.emailVerified || profile.isVerified || profile.verificationOverride);

        setResumeProgress({ ...(progress || {}), ...(profile || {}), exists: true });
        const deliveryStatus = String(profile.verificationDeliveryStatus || progress?.verificationDeliveryStatus || '').toLowerCase();
        setVerificationSent(verified || ['sent', 'already_verified'].includes(deliveryStatus));
        setIsEmailVerified(verified);

        if (verified) {
            const resumedStage = String(profile.registrationStage || profile.stage || progress?.registrationStage || progress?.stage || 'profile_completion');
            if (resumedStage.includes('business') || resumedStage.includes('company_') || profile.profileCompleted === true) setActiveStep(2);
            else setActiveStep(1);
            toast.success('Existing incomplete registration restored. Continue from where you stopped.', toastConfig.success);
            return user;
        }

        try {
            const delivery = await sendConfiguredVerification(user);
            setVerificationSent(true);
            if (delivery?.pendingManualVerification || verificationProvider === 'manual') {
                toast.success('Your account was restored and is awaiting Super Admin verification.', toastConfig.success);
            } else {
                toast.success(verificationProvider === 'whatsapp' ? 'Existing account restored. A new WhatsApp verification code was sent.' : 'Existing account restored. A new verification message was sent.', toastConfig.success);
            }
        } catch (deliveryError) {
            setVerificationSent(false);
            setError(friendlyError(deliveryError, 'Your existing account was restored, but verification delivery failed. Use Resend to try again.'));
        }
        startVerificationCheck(user);
        return user;
    };

    const createAccountAndSendVerification = async () => {
        const normalizedEmail = String(formData.email || '').trim().toLowerCase();
        if (!formData.firstName || !formData.lastName || !normalizedEmail || !formData.password || !formData.confirmPassword) {
            setError('Please fill in all required fields.');
            return;
        }

        if (loadingSettings) {
            setError('Registration settings are still loading. Please try again in a moment.');
            return;
        }

        const passwordErrors = [];
        if (formData.password.length < passwordPolicy.minLength) passwordErrors.push(`at least ${passwordPolicy.minLength} characters`);
        if (passwordPolicy.requireUppercase && !/[A-Z]/.test(formData.password)) passwordErrors.push('one uppercase letter');
        if (passwordPolicy.requireLowercase && !/[a-z]/.test(formData.password)) passwordErrors.push('one lowercase letter');
        if (passwordPolicy.requireNumbers && !/\d/.test(formData.password)) passwordErrors.push('one number');
        if (passwordPolicy.requireSpecialChars && !/[^A-Za-z0-9]/.test(formData.password)) passwordErrors.push('one special character');
        if (passwordErrors.length) {
            setError(`Password must contain ${passwordErrors.join(', ')}.`);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (verificationProvider === 'whatsapp' && !formData.phone.trim()) {
            setError('Enter the WhatsApp number you want to use for account verification.');
            return;
        }

        if (!acceptTerms) {
            setError('Please accept the Terms of Service and Privacy Policy.');
            return;
        }

        setIsLoading(true);
        setError('');
        setFormData((previous) => ({ ...previous, email: normalizedEmail }));

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, formData.password);
            const user = userCredential.user;
            setCreatedUser(user);

            // Persist the incomplete registration BEFORE any verification delivery.
            // This guarantees Super Admin can see and recover the account even if
            // the external email/WhatsApp gateway is unavailable.
            await saveRegistrationProgress({
                stage: 'email_verification',
                draft: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    registrationMode,
                    phone: formData.phone,
                    dialCode: formData.dialCode,
                    countryCode: formData.countryCode,
                },
                origin: window.location.origin,
            });

            try {
                const delivery = await sendConfiguredVerification(user);
                setVerificationSent(true);
                if (delivery?.pendingManualVerification || verificationProvider === 'manual') {
                    toast.success('Account created. Verification is pending Super Admin review.', toastConfig.success);
                } else {
                    toast.success(verificationProvider === 'whatsapp' ? 'WhatsApp verification code sent.' : 'Verification email sent! Please check your inbox.', toastConfig.success);
                }
            } catch (mailError) {
                console.error('Verification delivery failed:', mailError);
                setVerificationSent(false);
                setError(friendlyError(mailError, 'Your account was created and saved, but the verification message could not be sent. Use Resend to try again.'));
            }

            startVerificationCheck(user);
        } catch (err) {
            console.error('Error creating account:', err);
            if (err.code === 'auth/email-already-in-use') {
                try {
                    await restoreExistingAccountForRegistration(normalizedEmail, formData.password);
                } catch (resumeError) {
                    const code = String(resumeError?.code || '');
                    if (code.includes('wrong-password') || code.includes('invalid-credential') || code.includes('invalid-login-credentials')) {
                        setError('This email already has an account. Enter the same password to continue the incomplete registration, or use Sign In / Forgot Password.');
                    } else {
                        setError(friendlyError(resumeError, 'This email already has an account. Sign in to continue your registration.'));
                    }
                }
            } else {
                setError(friendlyError(err, 'Failed to create account. Please try again.'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const startVerificationCheck = (user) => {
        if (verificationProvider === 'whatsapp') return;
        let count = 0;
        const interval = setInterval(async () => {
            count++;
            setVerificationCheckCount(count);
            try {
                let verified = false;
                if (verificationProvider === 'firebase_auth') {
                    await user.reload();
                    verified = Boolean(auth.currentUser?.emailVerified);
                } else {
                    const snap = await getDoc(doc(db, 'users', user.uid));
                    const data = snap.exists() ? snap.data() || {} : {};
                    verified = Boolean(data.isVerified || data.emailVerified || data.verificationOverride);
                }
                if (verified) {
                    clearInterval(interval);
                    setVerificationInterval(null);
                    try {
                        await saveRegistrationProgress({
                            stage: 'profile_completion',
                            draft: { firstName: formData.firstName, lastName: formData.lastName, registrationMode },
                            origin: window.location.origin,
                        });
                        setIsEmailVerified(true);
                        toast.success('Account verified successfully!', toastConfig.success);
                        setTimeout(() => setActiveStep(1), 500);
                    } catch (progressError) {
                        console.error('Verified account progress save failed:', progressError);
                        setIsEmailVerified(true);
                        setError(friendlyError(progressError, 'Your email is verified, but TELCOFAIR could not save the next registration stage. Use Check Verification again before continuing.'));
                    }
                } else if (count > 24) {
                    clearInterval(interval);
                    setVerificationInterval(null);
                    toast.error(verificationProvider === 'manual' ? 'Verification is still awaiting Super Admin review.' : 'Verification is still pending. Use Resend or check again later.', toastConfig.error);
                }
            } catch (err) {
                console.error('Verification status check failed:', err);
            }
        }, 5000);
        setVerificationInterval(interval);
    };

    const verifyRegistrationWhatsAppOtp = async () => {
        if (!formData.phone.trim() || !registrationWhatsAppOtp.trim()) {
            setError('Enter your WhatsApp number and OTP.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            await verifyAccountWithWhatsAppOtp(formData.phone.trim(), registrationWhatsAppOtp.trim());
            setIsEmailVerified(true);
            if (verificationInterval) clearInterval(verificationInterval);
            await saveRegistrationProgress({
                stage: 'profile_completion',
                draft: { firstName: formData.firstName, lastName: formData.lastName, registrationMode, phone: formData.phone },
                origin: window.location.origin,
            });
            toast.success('WhatsApp verification completed.', toastConfig.success);
            setActiveStep(1);
        } catch (error) {
            setError(friendlyError(error, 'The WhatsApp verification code is invalid or expired.'));
        } finally { setIsLoading(false); }
    };

    const resendVerification = async () => {
        setIsLoading(true);
        try {
            if (createdUser) {
                await sendConfiguredVerification(createdUser);
                toast.success(verificationProvider === 'whatsapp' ? 'WhatsApp verification code resent!' : 'Verification message resent!', toastConfig.success);
                setVerificationCheckCount(0);
                // Restart verification check
                if (verificationInterval) {
                    clearInterval(verificationInterval);
                }
                startVerificationCheck(createdUser);
            } else {
                setError('No user found. Please try creating an account again.');
            }
        } catch (err) {
            console.error('Error resending verification:', err);
            setError(friendlyError(err, 'Failed to resend the verification email. Please try again.'));
        } finally {
            setIsLoading(false);
        }
    };

    // ============================================
    // STEP 2: Profile Completion
    // ============================================

    const validateStep2 = () => {
        if (!formData.title) return 'Please select your title.';
        if (!formData.country) return 'Please select your country.';
        if (!formData.companyName) return 'Please enter your company name or select a company.';
        if (registrationMode === 'create' && !formData.companyWebsite) return 'Please enter the company’s complete website URL. TELCOFAIR uses it as the unique company identifier.';
        if (!formData.businessTitle) return 'Please select your business title.';
        if (formData.specialization.length === 0) return 'Please select at least one specialization.';
        if (formData.primaryRegionAvailability.length === 0) return 'Please select at least one primary region.';
        if (formData.primaryServiceProvided.length === 0) return 'Please select at least one primary service.';
        
        // NEW: Validate LinkedIn URL if provided
        if (formData.linkedinProfile && formData.linkedinProfile.trim() !== '') {
            if (!isValidLinkedInUrl(formData.linkedinProfile)) {
                return 'Please enter a valid LinkedIn profile URL (e.g., https://linkedin.com/in/username)';
            }
        }
        return '';
    };

    const handleCompanySelect = (company) => {
        if (company) {
            setFormData({ ...formData, companyName: company.companyName });
            setSelectedCompany(company);
            setRegistrationMode('join');
            setShowSubscriptionStep(false);
            setIsPaymentRequired(false);
        } else {
            setFormData({ ...formData, companyName: '' });
            setSelectedCompany(null);
            setRegistrationMode('create');
            setShowSubscriptionStep(true);
            setIsPaymentRequired(Boolean(subscriptionSettings.paymentRequired));
        }
    };

    const handleCreateCompany = () => {
        setRegistrationMode('create');
        setSelectedCompany(null);
        setFormData({ ...formData, companyName: '' });
        setShowSubscriptionStep(true);
        setIsPaymentRequired(Boolean(subscriptionSettings.paymentRequired));
    };

    // ============================================
    // STEP 3: Buying Interests
    // ============================================

    const validateStep3 = () => {
        if (formData.preferredRegion.length === 0) return 'Please select at least one preferred region.';
        if (formData.primaryServiceBuying.length === 0) return 'Please select at least one service you\'re interested in buying.';
        return '';
    };

    // ============================================
    // PREPARE REGISTRATION DATA (without saving)
    // ============================================

    const prepareRegistrationData = async () => {
        setIsLoading(true);
        setError('');

        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                setError('User session expired. Please login again.');
                setIsLoading(false);
                return null;
            }

            // Never allow profile/company provisioning to be used as a client-side
            // bypass around the configured account-verification provider.
            await assertRegistrationVerified();

            // Upload images
            let profileImageUrl = null;
            let companyLogoUrl = null;
            let companyCoverUrl = null;

            if (profileImageFile) {
                const path = `profile_images/${Date.now()}_${profileImageFile.name}`;
                profileImageUrl = await uploadImage(profileImageFile, path);
            }

            if (companyLogoFile && registrationMode === 'create') {
                const path = `company_logos/${Date.now()}_${companyLogoFile.name}`;
                companyLogoUrl = await uploadImage(companyLogoFile, path);
            }

            if (companyCoverFile && registrationMode === 'create') {
                const path = `company_covers/${Date.now()}_${companyCoverFile.name}`;
                companyCoverUrl = await uploadImage(companyCoverFile, path);
            }

            const fullName = `${formData.firstName} ${formData.lastName}`;
            const uid = currentUser.uid;

            let userData = {};
            let joinResult = null;

            if (registrationMode === 'create') {
                if (formData.companyWebsite) {
                    const { exists, url } = await checkCompanyExistsByUrl(formData.companyWebsite);
                    if (exists) {
                        setError('This company URL is already registered. Please select the company from the list instead.');
                        setIsLoading(false);
                        return null;
                    }
                    formData.companyWebsite = url;
                }

                const companyPayload = {
                    companyName: formData.companyName,
                    companyType: formData.companyType || 'Other',
                    website: formData.companyWebsite || '',
                    country: formData.country?.name || '',
                    countryCode: formData.country?.code || '',
                    logo: companyLogoUrl,
                    logoUrl: companyLogoUrl,
                    companyLogo: companyLogoUrl,
                    companyLogoUrl: companyLogoUrl,
                    coverImage: companyCoverUrl,
                    coverImageUrl: companyCoverUrl,
                    companyCover: companyCoverUrl,
                    companyCoverUrl: companyCoverUrl,
                    adminId: uid,
                    adminEmail: formData.email,
                    adminName: fullName,
                    // Persist the company-facing business profile as well as the
                    // administrator's user profile. Super Admin certificates,
                    // company cards and matching logic all read from companies/{id}.
                    specialization: [...formData.specialization],
                    primaryRegionAvailability: [...formData.primaryRegionAvailability],
                    secondaryRegionAvailability: [...formData.secondaryRegionAvailability],
                    preferredRegion: [...formData.preferredRegion],
                    secondaryPreferredRegion: [...formData.secondaryPreferredRegion],
                    primaryServiceProvided: [...formData.primaryServiceProvided],
                    secondaryServiceProvided: [...formData.secondaryServiceProvided],
                    primaryServiceBuying: [...formData.primaryServiceBuying],
                    secondaryServiceBuying: [...formData.secondaryServiceBuying],
                    servicesProvided: Array.from(new Set([
                        ...formData.primaryServiceProvided,
                        ...formData.secondaryServiceProvided,
                    ])),
                    regionPriorities: Array.from(new Set([
                        ...formData.primaryRegionAvailability,
                        ...formData.secondaryRegionAvailability,
                        ...formData.preferredRegion,
                        ...formData.secondaryPreferredRegion,
                    ])),
                    lookingFor: Array.from(new Set([
                        ...formData.primaryServiceBuying,
                        ...formData.secondaryServiceBuying,
                    ])),
                    isActive: !isPaymentRequired, // Payment-gated activation
                    isVerified: false,
                    verificationStatus: 'pending',
                    isBlocked: false,
                    departments: ['Executive', 'Sales', 'Marketing', 'Engineering', 'Operations', 'Support', 'Finance', 'HR', 'Product', 'Design'],
                    designations: ['CEO', 'CTO', 'COO', 'VP', 'Director', 'Manager', 'Senior', 'Lead', 'Developer', 'Analyst', 'Associate', 'Intern'],
                    subscription: {
                        packageId: selectedPackage?.id || null,
                        packageName: selectedPackage?.name || null,
                        price: selectedPackage?.price || 0,
                        currency: subscriptionSettings.currency || 'EUR',
                        billingCycle: selectedPackage?.billingCycle || 'one-time',
                        status: isPaymentRequired ? 'pending_payment' : 'active',
                        subscribedAt: serverTimestamp(),
                    },
                    paymentStatus: isPaymentRequired ? 'pending' : 'not_required',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                };

                // Create the company through the backend transaction so the normalized
                // HTTPS website remains unique even if two users submit at the same time.
                const createdCompany = await createRegistrationCompany({
                    company: companyPayload,
                    packageId: selectedPackage?.id || null,
                });
                const companyId = createdCompany.companyId;
                const paymentRequiredForUser = createdCompany.paymentRequired ?? isPaymentRequired;
                if (Boolean(paymentRequiredForUser) !== Boolean(isPaymentRequired)) {
                    setIsPaymentRequired(Boolean(paymentRequiredForUser));
                }
                // Keep the current browser session in sync with the server-created
                // company. If the payment dialog is closed and reopened we resume
                // this same company instead of attempting a duplicate registration.
                setResumeProgress((previous) => ({
                    ...(previous || {}),
                    exists: true,
                    profileCompleted: true,
                    companyId,
                    registrationPackageId: selectedPackage?.id || '',
                    registrationStatus: paymentRequiredForUser ? 'pending_company_subscription' : 'active',
                    paymentStatus: paymentRequiredForUser ? 'company_payment_pending' : 'not_required',
                    stage: paymentRequiredForUser ? 'company_subscription' : 'completed',
                    isActive: !paymentRequiredForUser,
                }));

                userData = {
                    uid,
                    email: String(formData.email || '').trim().toLowerCase(),
                    emailNormalized: String(formData.email || '').trim().toLowerCase(),
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    displayName: fullName,
                    title: formData.title,
                    country: formData.country?.name || '',
                    countryCode: formData.country?.code || '',
                    phone: formData.phone || '',
                    dialCode: formData.dialCode || '+1',
                    profileImage: profileImageUrl,
                    specialization: formData.specialization,
                    businessTitle: formData.businessTitle,
                    primaryRegionAvailability: formData.primaryRegionAvailability,
                    primaryServiceProvided: formData.primaryServiceProvided,
                    secondaryRegionAvailability: formData.secondaryRegionAvailability,
                    secondaryServiceProvided: formData.secondaryServiceProvided,
                    preferredRegion: formData.preferredRegion,
                    primaryServiceBuying: formData.primaryServiceBuying,
                    secondaryPreferredRegion: formData.secondaryPreferredRegion,
                    secondaryServiceBuying: formData.secondaryServiceBuying,
                    linkedinProfile: formData.linkedinProfile || '', // NEW: Save LinkedIn
                    companyId: companyId,
                    companyName: formData.companyName,
                    role: 'admin',
                    isOwner: true,
                    isActive: !paymentRequiredForUser,
                    isApproved: true,
                    seatStatus: paymentRequiredForUser ? 'reserved' : 'active',
                    registrationMode: 'create_company',
                    registrationStatus: paymentRequiredForUser ? 'pending_company_subscription' : 'active',
                    paymentStatus: paymentRequiredForUser ? 'company_payment_pending' : 'not_required',
                    profileCompleted: true,
                    registrationIncomplete: Boolean(paymentRequiredForUser),
                    registrationStage: paymentRequiredForUser ? 'company_subscription' : 'completed',
                    stage: paymentRequiredForUser ? 'company_subscription' : 'completed',
                    registrationReminderStoppedAt: paymentRequiredForUser ? null : serverTimestamp(),
                    registrationAbandonedAt: null,
                    registrationPackageId: selectedPackage?.id || null,
                    isVerified: true,
                    isBlocked: false,
                    isOnline: false,
                    joinedAt: serverTimestamp(),
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                };

                // Save user to Firestore
                await setDoc(doc(db, 'users', uid), userData, { merge: true });

            } else {
                if (!selectedCompany) {
                    setError('Please select a company to join.');
                    setIsLoading(false);
                    return null;
                }

                // Existing-company registration is FREE for the individual. The
                // backend atomically verifies the company subscription and reserves
                // one user seat. If the plan is full the registration is rejected.
                const joinProfile = {
                    uid,
                    email: String(formData.email || '').trim().toLowerCase(),
                    emailNormalized: String(formData.email || '').trim().toLowerCase(),
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    displayName: fullName,
                    title: formData.title,
                    country: formData.country?.name || '',
                    countryCode: formData.country?.code || '',
                    phone: formData.phone || '',
                    dialCode: formData.dialCode || '+1',
                    profileImage: profileImageUrl,
                    specialization: formData.specialization,
                    businessTitle: formData.businessTitle,
                    primaryRegionAvailability: formData.primaryRegionAvailability,
                    primaryServiceProvided: formData.primaryServiceProvided,
                    secondaryRegionAvailability: formData.secondaryRegionAvailability,
                    secondaryServiceProvided: formData.secondaryServiceProvided,
                    preferredRegion: formData.preferredRegion,
                    primaryServiceBuying: formData.primaryServiceBuying,
                    secondaryPreferredRegion: formData.secondaryPreferredRegion,
                    secondaryServiceBuying: formData.secondaryServiceBuying,
                    linkedinProfile: formData.linkedinProfile || '',
                };

                const joined = await joinExistingCompany({
                    companyId: selectedCompany.id,
                    profile: joinProfile,
                });

                joinResult = joined;
                setIsPaymentRequired(false);
                const needsExtraSeatPayment = Boolean(joined?.paymentRequired);
                userData = {
                    ...joinProfile,
                    companyId: selectedCompany.id,
                    companyName: selectedCompany.companyName,
                    role: 'employee',
                    isActive: false,
                    isApproved: false,
                    seatStatus: needsExtraSeatPayment ? 'pending_extra_seat_payment' : 'reserved',
                    registrationMode: 'join_company',
                    registrationStatus: joined?.status || (needsExtraSeatPayment ? 'pending_extra_seat_payment' : 'pending_company_approval'),
                    paymentStatus: needsExtraSeatPayment ? 'extra_seat_payment_pending' : 'not_required',
                    profileCompleted: true,
                    registrationIncomplete: true,
                    registrationStage: needsExtraSeatPayment ? 'extra_seat_payment' : 'company_approval',
                    stage: needsExtraSeatPayment ? 'extra_seat_payment' : 'company_approval',
                    registrationReminderStoppedAt: null,
                    extraSeatAmount: needsExtraSeatPayment ? Number(joined?.extraSeatAmount || 0) : 0,
                    extraSeatCurrency: joined?.extraSeatCurrency || '',
                };
            }

            // Return the user data for certificate
            return {
                userData,
                fullName,
                uid,
                companyName: registrationMode === 'create' ? formData.companyName : selectedCompany?.companyName,
                companyLogoUrl: registrationMode === 'create'
                    ? (companyLogoUrl || companyLogoPreview || '')
                    : (selectedCompany?.companyLogoUrl || selectedCompany?.logoUrl || selectedCompany?.companyLogo || selectedCompany?.logo || ''),
                paymentRequired: registrationMode === 'create' ? Boolean(['company_payment_pending', 'pending'].includes(userData?.paymentStatus)) : false,
                extraSeatPaymentRequired: registrationMode === 'join' && userData?.paymentStatus === 'extra_seat_payment_pending',
                extraSeatPayment: registrationMode === 'join' && userData?.paymentStatus === 'extra_seat_payment_pending' ? {
                    companyId: selectedCompany?.id,
                    amount: Number(userData?.extraSeatAmount || 0),
                    currency: userData?.extraSeatCurrency || 'EUR',
                    paymentLink: joinResult?.extraSeatPaymentLink || '',
                    instructions: joinResult?.extraSeatPaymentInstructions || '',
                } : null,
            };

        } catch (err) {
            console.error('Error preparing registration:', err);
            setError(friendlyError(err, 'Registration preparation failed. Please try again.'));
            setIsLoading(false);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // ============================================
    // COMPLETE REGISTRATION (after payment)
    // ============================================

    const completeRegistration = async (data) => {
        try {
            setIsLoading(true);

            if (registrationMode === 'create' && isPaymentRequired && data?.userData) {
                const status = await getRegistrationPaymentStatus();
                if (!status?.active) {
                    throw new Error('Company subscription payment verification is still pending. Please wait a moment and try again.');
                }
            }

            // New-company free plans are already activated by createRegistrationCompany.
            // Existing-company users remain pending company approval and must never be
            // activated locally, because the reserved-seat/approval transaction is server-side.
            if (registrationMode === 'create') {
                try { await login(formData.email, formData.password); } catch (_) { /* Firebase session may already be active */ }
            }

            const companyIdForCertificate = data?.userData?.companyId || resumeProgress?.companyId || '';
            const stableCertificateId = String(companyIdForCertificate || data?.uid || auth.currentUser?.uid || 'COMPANY')
                .replace(/[^a-z0-9]/gi, '')
                .toUpperCase()
                .slice(-8)
                .padStart(6, '0');
            const registeredDataObj = {
                name: `${formData.firstName} ${formData.lastName}`,
                companyName: data?.companyName || (registrationMode === 'create' ? formData.companyName : selectedCompany?.companyName),
                companyId: companyIdForCertificate,
                companyType: formData.companyType || 'Other',
                companyLogo: data?.companyLogoUrl || companyLogoPreview || '',
                type: registrationMode === 'create' ? 'company' : 'user',
                role: registrationMode === 'create' ? 'Company Admin' : 'Team Member · Approval Pending',
                adminName: `${formData.firstName} ${formData.lastName}`,
                date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                registrationNumber: `TFC-${stableCertificateId}-${new Date().getFullYear()}`,
                services: Array.from(new Set([
                    ...formData.primaryServiceProvided,
                    ...formData.secondaryServiceProvided,
                ].filter(Boolean))),
                regionPriorities: Array.from(new Set([
                    ...formData.primaryRegionAvailability,
                    ...formData.secondaryRegionAvailability,
                    ...formData.preferredRegion,
                    ...formData.secondaryPreferredRegion,
                ].filter(Boolean))),
                lookingFor: Array.from(new Set([
                    ...formData.primaryServiceBuying,
                    ...formData.secondaryServiceBuying,
                ].filter(Boolean))),
                package: registrationMode === 'create' && selectedPackage ? {
                    name: selectedPackage.name,
                    price: selectedPackage.price,
                    currency: subscriptionSettings.currency || 'USD',
                    billingCycle: selectedPackage.billingCycle || 'monthly',
                    userLimit: selectedPackage.userLimit ?? selectedPackage.maxUsers ?? null,
                } : null
            };

            setRegisteredData(registeredDataObj);
            setShowCertificate(true);
            setIsLoading(false);
            toast.success(
                registrationMode === 'create'
                    ? 'Company registration completed successfully!'
                    : data?.extraSeatPaymentRequired ? 'Additional seat payment submitted. Your company administrator must review the payment and approve the seat.' : 'Registration submitted. Your company administrator must approve your reserved seat.',
                toastConfig.success
            );
        } catch (err) {
            console.error('Error completing registration:', err);
            setError(friendlyError(err, 'Registration activation is still pending.'));
            setIsLoading(false);
        }
    };

    // ============================================
    // MAIN REGISTRATION FLOW
    // ============================================

    const handleRegistration = async () => {
        const currentStatuses = [
            String(resumeProgress?.registrationStatus || '').toLowerCase(),
            String(resumeProgress?.paymentStatus || '').toLowerCase(),
        ];
        const pendingResume = Boolean(
            resumeProgress?.profileCompleted &&
            resumeProgress?.companyId &&
            ['pending_company_subscription', 'company_payment_pending', 'company_payment_pending_review', 'pending_payment', 'pending_verification', 'company_payment_rejected', 'rejected', 'pending'].some((value) => currentStatuses.includes(value))
        );

        if (pendingResume) {
            if (!selectedPackage) {
                setError('Your company is already created. Select the saved subscription package to resume payment.');
                return;
            }
            setError('');
            setShowPaymentDialog(true);
            return;
        }

        const validationError = validateStep3();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');

        // Subscription purchase applies only when this person is registering a NEW company.
        // A person joining an existing subscribed company never pays personally.
        if (registrationMode === 'create' && !selectedPackage) {
            setError('Please select a company subscription plan to continue.');
            return;
        }

        setIsSubmitting(true);
        const data = await prepareRegistrationData();
        setIsSubmitting(false);
        if (!data) return;
        setRegistrationData(data);

        if (data.extraSeatPaymentRequired && data.extraSeatPayment) {
            setExtraSeatPayment(data.extraSeatPayment);
            return;
        }

        const paymentRequiredNow = data.paymentRequired ?? isPaymentRequired;
        if (paymentRequiredNow && Number(selectedPackage.price || 0) > 0) {
            setIsPaymentRequired(true);
            setShowPaymentDialog(true);
            return;
        }

        // Free/no-payment package is activated through the backend as well when
        // payment mode is enabled, otherwise complete locally.
        if (paymentRequiredNow) {
            try {
                await startRegistrationPayment({
                    packageId: selectedPackage.id,
                    customer: { name: data.fullName, email: formData.email, phone: formData.phone },
                    themeColor: palette.rust,
                });
            } catch (paymentError) {
                setError(friendlyError(paymentError, 'Unable to activate registration package.'));
                return;
            }
        }
        await completeRegistration(data);
    };

    const handlePaymentSuccess = async () => {
        setPaymentCompleted(true);
        setShowPaymentDialog(false);
        
        // Complete registration with the stored data
        if (registrationData) {
            await completeRegistration(registrationData);
        } else {
            // Fallback: prepare and complete
            const data = await prepareRegistrationData();
            if (data) {
                await completeRegistration(data);
            }
        }
    };

    const handleManualPaymentPending = (payment) => {
        setShowPaymentDialog(false);
        setPaymentCompleted(false);
        setResumeProgress((previous) => ({
            ...(previous || {}),
            companyId: payment?.companyId || previous?.companyId || registrationData?.companyId || '',
            paymentStatus: 'company_payment_pending_review',
            registrationStatus: 'pending_company_subscription',
            registrationStage: 'company_subscription',
            stage: 'company_subscription',
            profileCompleted: true,
        }));
        setError('Payment proof submitted successfully. Your account will activate after Super Admin verifies the UPI / bank-transfer payment. You can safely close this page and return later.');
    };

    const handlePaymentCancel = () => {
        setShowPaymentDialog(false);
        setError('Company subscription payment is required to activate this new company.');
    };

    const downloadRegistrationCertificate = async () => {
        if (registrationMode !== 'create' || !registrationCertificateRef.current || certificateDownloading) return;
        setCertificateDownloading(true);
        try {
            const node = registrationCertificateRef.current;
            const canvas = await html2canvas(node, {
                scale: Math.min((window.devicePixelRatio || 1) * 1.6, 3),
                useCORS: true,
                logging: false,
                backgroundColor: '#FFFFFF',
                windowWidth: node.scrollWidth,
                windowHeight: node.scrollHeight,
            });
            const image = canvas.toDataURL('image/jpeg', 0.96);
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 24;
            const fit = Math.min((pageW - margin * 2) / canvas.width, (pageH - margin * 2) / canvas.height);
            const width = canvas.width * fit;
            const height = canvas.height * fit;
            pdf.addImage(image, 'JPEG', (pageW - width) / 2, (pageH - height) / 2, width, height);
            const safeName = String(registeredData?.companyName || 'Company').replace(/[^a-z0-9_-]+/gi, '_');
            pdf.save(`TELCOFAIR_Certificate_${safeName}.pdf`);
            toast.success('Certificate downloaded successfully!', toastConfig.success);
        } catch (certificateError) {
            console.error('Certificate download failed:', certificateError);
            toast.error('Unable to download the certificate. Please try again.');
        } finally {
            setCertificateDownloading(false);
        }
    };

    const handleCertificateContinue = async () => {
        if (registrationMode === 'join') {
            try { await signOut(auth); } catch (_) { /* ignore */ }
            navigate('/login', { replace: true });
            return;
        }
        navigate('/dashboard');
    };

    // ============================================
    // STYLES
    // ============================================

    const textFieldSx = {
        '& .MuiInputLabel-root': { color: palette.inkLabel, fontSize: '0.78rem', fontWeight: 500, transition: 'all 0.25s ease' },
        '& .MuiInputLabel-root.Mui-focused': { color: palette.rust },
        '& .MuiOutlinedInput-root': {
            ...glassWell('12px'),
            transition: `all 0.28s ${EASE}`,
            '& fieldset': { border: 'none' },
            '&:hover': {
                borderColor: alpha(palette.rust, 0.3),
                boxShadow: `inset 0 2px 8px rgba(${SHADOW},0.08), inset 0 -1px 0 rgba(255,255,255,0.7)`,
            },
            '&.Mui-focused': {
                transform: 'translateY(-1px)',
                borderColor: alpha(palette.rust, 0.4),
                boxShadow: `
                    inset 0 1px 4px rgba(${SHADOW}, 0.05),
                    0 8px 20px rgba(${SHADOW}, 0.08),
                    0 0 0 2px ${alpha(palette.rust, 0.1)}
                `,
            },
            '& input, & textarea': { padding: '10px 12px', fontSize: '0.82rem', fontWeight: 500, color: palette.ink },
            '& input:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 100px ${alpha(palette.canvasAlt, 0.92)} inset`,
                WebkitTextFillColor: palette.ink,
                transition: 'background-color 9999s ease-in-out 0s',
            },
        },
        '& .MuiFormHelperText-root': { color: palette.inkFaint, fontSize: '0.65rem', ml: 0.5 },
    };

    const pickerButtonSx = (active) => ({
        height: '44px',
        ...glassWell('12px', active ? palette.rust : null),
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        textTransform: 'none',
        color: palette.ink,
        fontWeight: 500,
        fontSize: '0.8rem',
        transition: `all 0.28s ${EASE}`,
        '&:hover': {
            borderColor: alpha(palette.rust, 0.35),
            transform: 'translateY(-1px)',
            boxShadow: `0 8px 18px rgba(${SHADOW},0.08), inset 0 1px 0 rgba(255,255,255,0.7)`,
        },
    });

    const alertSx = (color) => ({
        borderRadius: '12px',
        bgcolor: alpha(color, 0.06),
        backdropFilter: 'blur(8px) saturate(160%)',
        WebkitBackdropFilter: 'blur(8px) saturate(160%)',
        border: `1px solid ${alpha(color, 0.2)}`,
        boxShadow: `0 4px 12px ${alpha(color, 0.06)}, inset 0 1px 0 rgba(255,255,255,0.6)`,
        color: palette.inkSoft,
        '& .MuiAlert-icon': { color },
        '& .MuiAlert-message': { fontSize: '0.78rem' },
    });

    const scrollAreaSx = (color) => ({
        maxHeight: '50vh',
        overflowY: 'auto',
        pr: 0.5,
        mx: -0.5,
        px: 0.5,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { background: alpha(color, 0.3), borderRadius: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
    });

    const slideVariants = {
        enter: (dir) => ({ x: dir > 0 ? 30 : -30, opacity: 0 }),
        center: { x: 0, opacity: 1, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
        exit: (dir) => ({ x: dir > 0 ? -30 : 30, opacity: 0, transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] } }),
    };

    const fieldVariants = {
        enter: { opacity: 0, y: 10 },
        center: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.04, duration: 0.35, ease: [0.16, 1, 0.3, 1] } }),
    };

    // ============================================
    // RENDER: CERTIFICATE VIEW
    // ============================================

    if (showCertificate && registeredData) {
        return (
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, overflow: 'auto' }}>
                <AuroraBackdrop />
                <motion.div initial={{ scale: 0.85, opacity: 0, y: 40 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={{ duration: 0.6, type: 'spring', bounce: 0.3 }} style={{ width: '100%', maxWidth: 980, position: 'relative', zIndex: 1 }}>
                    <Paper elevation={0} sx={{
                        ...glassCard({ level: 4, radius: '28px', accent: palette.rust }),
                        width: '100%',
                        overflow: 'hidden',
                        p: { xs: 3, sm: 4 },
                        textAlign: 'center',
                    }}>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.3, duration: 0.6, type: 'spring' }}>
                                <Box sx={{ position: 'relative', width: 90, height: 90, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {[0, 1, 2].map((i) => (
                                        <Box key={i} sx={{
                                            position: 'absolute', width: 80, height: 80, borderRadius: '50%',
                                            border: `2px solid ${palette.rust}`, opacity: 0,
                                            animation: `tfSignalWave 2.6s cubic-bezier(0.2,0.6,0.3,1) ${i * 0.7}s infinite`,
                                        }} />
                                    ))}
                                    <Box sx={{ ...glassPod(palette.rust, 76, '50%'), animation: 'tfBreathe 3.6s ease-in-out infinite', zIndex: 1 }}>
                                        <EmojiEventsIcon sx={{ fontSize: 36 }} />
                                    </Box>
                                </Box>
                            </motion.div>

                            <Typography component="div" sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '0.75rem', color: palette.inkFaint,
                                letterSpacing: 3, textTransform: 'uppercase', mb: 0.5,
                            }}>
                                🎉 Congratulations!
                            </Typography>

                            <Typography component="div" sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: { xs: '1.6rem', sm: '2rem' },
                                fontWeight: 700, color: palette.ink, lineHeight: 1.1, mb: 0.5,
                            }}>
                                Registration Complete!
                            </Typography>

                            <Typography component="div" sx={{ fontSize: '0.9rem', color: palette.inkSoft, mb: 0.5 }}>
                                {registrationMode === 'create' ? (
                                    <>🎉 <strong>{formData.companyName}</strong> is now registered<br />
                                    <span style={{ fontSize: '0.75rem', color: palette.teal }}>Admin: {formData.firstName} {formData.lastName}</span></>
                                ) : (
                                    <>{formData.firstName} {formData.lastName} is now a registered member at <strong>{formData.companyName}</strong></>
                                )}
                            </Typography>

                            <Box sx={{ width: 40, height: 2, mx: 'auto', my: 2, background: `linear-gradient(90deg, ${palette.rust}, ${palette.gold})`, borderRadius: 2 }} />

                            {registrationMode === 'create' && (
                                <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.45 }}>
                                    <Box
                                        ref={registrationCertificateRef}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            maxWidth: 860,
                                            mx: 'auto',
                                            mb: 2.5,
                                            p: { xs: 2.2, sm: 3 },
                                            borderRadius: '20px',
                                            bgcolor: '#FFFFFF',
                                            border: `1px solid ${alpha(palette.rust, 0.16)}`,
                                            boxShadow: '0 18px 48px rgba(25,57,78,.10)',
                                            textAlign: 'left',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: 0,
                                                background: `radial-gradient(circle at 10% 5%, ${alpha(palette.gold, .10)} 0, transparent 30%), radial-gradient(circle at 92% 90%, ${alpha(palette.teal, .08)} 0, transparent 34%)`,
                                                pointerEvents: 'none',
                                            },
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: 10,
                                                border: `1px solid ${alpha(palette.rust, .10)}`,
                                                borderRadius: '14px',
                                                pointerEvents: 'none',
                                            },
                                        }}
                                    >
                                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                                                    <BrandLogo scope="platform" size={54} radius={13} alt="TELCOFAIR" />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontWeight: 900, color: palette.ink, fontSize: '.88rem', letterSpacing: '-.02em' }}>TELCOFAIR</Typography>
                                                        <Typography sx={{ fontSize: '.5rem', color: palette.inkFaint, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>Network Registry</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ width: 1, height: 48, bgcolor: alpha(palette.ink, .09) }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                                                    <Box sx={{ minWidth: 0, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                                                        <Typography sx={{ fontWeight: 900, color: palette.ink, fontSize: '.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{registeredData.companyName}</Typography>
                                                        <Typography sx={{ fontSize: '.5rem', color: palette.inkFaint, fontWeight: 750 }}>{registeredData.companyType}</Typography>
                                                    </Box>
                                                    {registeredData.companyLogo ? (
                                                        <Box component="img" src={registeredData.companyLogo} alt={`${registeredData.companyName} logo`} crossOrigin="anonymous" sx={{ width: 58, height: 58, objectFit: 'contain', borderRadius: '12px', p: .65, bgcolor: '#fff', border: `1px solid ${alpha(palette.ink,.10)}` }} />
                                                    ) : (
                                                        <Box sx={{ width: 58, height: 58, display: 'grid', placeItems: 'center', borderRadius: '12px', bgcolor: alpha(palette.rust,.08), color: palette.rust }}><BusinessIcon /></Box>
                                                    )}
                                                </Box>
                                            </Box>

                                            <Box sx={{ textAlign: 'center', mt: 2.2, mb: 2 }}>
                                                <Typography sx={{ fontSize: '.58rem', fontWeight: 900, color: palette.goldDeep || palette.gold, letterSpacing: '.18em', textTransform: 'uppercase' }}>Certificate of Registration</Typography>
                                                <Typography sx={{ mt: .5, fontSize: { xs: '1.35rem', sm: '1.75rem' }, lineHeight: 1.12, fontWeight: 900, color: palette.ink, letterSpacing: '-.035em' }}>{registeredData.companyName}</Typography>
                                                <Typography sx={{ mt: .55, fontSize: '.68rem', color: palette.inkSoft }}>Registered on TELCOFAIR · {registeredData.date}</Typography>
                                                <Typography sx={{ mt: .35, fontSize: '.58rem', color: palette.inkFaint, fontFamily: 'monospace', fontWeight: 800 }}>{registeredData.registrationNumber}</Typography>
                                            </Box>

                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' }, gap: 1.1 }}>
                                                {[
                                                    { label: 'Services', values: registeredData.services || [], tone: palette.gold },
                                                    { label: 'Region priorities', values: registeredData.regionPriorities || [], tone: palette.teal },
                                                    { label: 'Looking for', values: registeredData.lookingFor || [], tone: palette.rust },
                                                ].map((section) => (
                                                    <Box key={section.label} sx={{ minHeight: 112, p: 1.35, borderRadius: '13px', bgcolor: '#fff', border: `1px solid ${alpha(section.tone,.14)}`, boxShadow: `0 8px 20px ${alpha(section.tone,.055)}` }}>
                                                        <Typography sx={{ fontSize: '.54rem', fontWeight: 900, color: section.tone, textTransform: 'uppercase', letterSpacing: '.12em', mb: .75 }}>{section.label}</Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .45 }}>
                                                            {section.values.length ? section.values.map((value) => (
                                                                <Box key={`${section.label}-${value}`} sx={{ maxWidth: '100%', px: .8, py: .4, borderRadius: '999px', bgcolor: alpha(section.tone,.07), border: `1px solid ${alpha(section.tone,.12)}`, color: palette.inkSoft, fontSize: '.56rem', lineHeight: 1.25, fontWeight: 750, overflowWrap: 'anywhere' }}>{value}</Box>
                                                            )) : <Typography sx={{ fontSize: '.62rem', color: palette.inkFaint, fontStyle: 'italic' }}>Not specified</Typography>}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>

                                            {registeredData.package && (
                                                <Box sx={{ mt: 1.4, display: 'flex', justifyContent: 'center' }}>
                                                    <Box sx={{ px: 1.4, py: .65, borderRadius: '999px', bgcolor: alpha(palette.teal,.07), border: `1px solid ${alpha(palette.teal,.12)}` }}>
                                                        <Typography sx={{ fontSize: '.58rem', color: palette.inkSoft, fontWeight: 750 }}>
                                                            Subscription: <Box component="span" sx={{ color: palette.teal, fontWeight: 900 }}>{registeredData.package.name}</Box>
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}

                                            <Box sx={{ mt: 1.8, pt: 1.2, borderTop: `1px solid ${alpha(palette.ink,.08)}`, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: '.55rem', color: palette.inkFaint }}>This certificate reflects the company profile recorded in the TELCOFAIR network.</Typography>
                                                <Typography sx={{ fontSize: '.55rem', color: palette.teal, fontWeight: 900, letterSpacing: '.08em' }}>DIGITALLY RECORDED</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, maxWidth: 450, mx: 'auto', mb: 3 }}>
                                {[
                                    { label: 'Role', value: registrationMode === 'create' ? 'Admin' : 'Team Member', color: palette.teal },
                                    { label: 'Company', value: formData.companyName, color: palette.rust },
                                    { label: 'Status', value: '✅ Active', color: palette.green },
                                ].map((item) => (
                                    <Box key={item.label} sx={{ ...glassCard({ level: 1, radius: '10px' }), p: 1.5, textAlign: 'center' }}>
                                        <Typography component="div" sx={{ fontSize: '0.55rem', color: palette.inkFaint, fontWeight: 600, textTransform: 'uppercase' }}>
                                            {item.label}
                                        </Typography>
                                        <Typography component="div" sx={{ fontSize: '0.75rem', color: item.color, fontWeight: 700 }}>{item.value}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 1.5 }}>
                                {registrationMode === 'create' && (
                                    <Button
                                        onClick={downloadRegistrationCertificate}
                                        disabled={certificateDownloading}
                                        startIcon={certificateDownloading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DownloadIcon />}
                                        sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 3, py: 0.8, fontSize: '0.8rem' }}
                                    >
                                        {certificateDownloading ? 'Preparing Certificate…' : 'Download Certificate'}
                                    </Button>
                                )}
                                <Button
                                    onClick={handleCertificateContinue}
                                    endIcon={<RocketIcon />}
                                    sx={{ ...glassButton(palette.teal, palette.tealDeep), px: 3, py: 0.8, fontSize: '0.8rem' }}
                                >
                                    🚀 Go to Dashboard
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </motion.div>
            </Box>
        );
    }

    // ============================================
    // RENDER: MAIN FORM
    // ============================================

    return (
        <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <AuroraBackdrop />

            <Box sx={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>

                {/* LEFT SIDE — BRANDING */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, width: '45%', flexShrink: 0, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative', px: 4, py: 4, overflow: 'hidden' }}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                            <Box sx={{ position: 'relative', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {[0, 1, 2].map((i) => (
                                    <Box key={i} sx={{
                                        position: 'absolute', width: 48, height: 48, borderRadius: '50%',
                                        border: `2px solid ${palette.rust}`, opacity: 0,
                                        animation: `tfSignalWave 2.6s cubic-bezier(0.2,0.6,0.3,1) ${i * 0.7}s infinite`,
                                    }} />
                                ))}
                                <BrandLogo scope="platform" size={48} radius={16} alt="TELCOFAIR" sx={{ animation: 'tfBreathe 3.6s ease-in-out infinite', zIndex: 1 }} />
                            </Box>
                            <Typography component="div" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, color: palette.ink, letterSpacing: '-0.02em', fontSize: '2.2rem', lineHeight: 1 }}>
                                TELCO<span style={{ color: palette.rust }}>FAIR</span>
                            </Typography>
                        </Box>

                        <Typography component="div" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, color: palette.ink, fontSize: '2.6rem', lineHeight: 1.1, mb: 2, letterSpacing: '-0.03em' }}>
                            Join the <Box component="span" sx={{ color: palette.rust }}>Telecom</Box> <Box component="span" sx={{ color: palette.teal }}>Network</Box>
                        </Typography>
                        <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.95rem', lineHeight: 1.6, mb: 4, maxWidth: '90%', fontWeight: 400 }}>
                            Register your company or join as a team member to connect with 1,200+ telecom companies globally.
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {[
                                { icon: <PersonIcon sx={{ fontSize: 16 }} />, title: 'Create Your Account', desc: 'Sign up with your name and email', color: palette.rust },
                                { icon: <VerifiedIcon sx={{ fontSize: 16 }} />, title: 'Verify Your Email', desc: 'Confirm your email address to continue', color: palette.teal },
                                { icon: <BusinessIcon sx={{ fontSize: 16 }} />, title: 'Complete Your Profile', desc: 'Tell us about your business and interests', color: palette.green },
                            ].map((feat, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                                    <Box sx={{
                                        ...glassCard({ level: 1, radius: '14px', accent: feat.color }),
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 1.5,
                                        overflow: 'hidden',
                                        transition: `transform 0.3s ${EASE}, box-shadow 0.3s ease`,
                                        '&:hover': {
                                            transform: 'translateX(4px) translateY(-2px)',
                                            ...glassLift(2, feat.color),
                                        },
                                    }}>
                                        <Box className="feat-pod" sx={glassPod(feat.color, 38, '12px')}>
                                            {feat.icon}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography component="div" sx={{ fontWeight: 700, color: palette.ink, fontSize: '0.85rem', lineHeight: 1.2, mb: 0.1 }}>{feat.title}</Typography>
                                            <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.72rem', lineHeight: 1.3, fontWeight: 400 }}>{feat.desc}</Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>
                    </motion.div>
                </Box>

                {/* RIGHT SIDE — REGISTRATION FORM */}
                <Box sx={{ width: { xs: '100%', md: '55%' }, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, md: 4 }, height: '100%', overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: alpha(palette.rust, 0.3), borderRadius: '4px' } }}>
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} style={{ width: '100%', maxWidth: 560 }}>
                        <Paper elevation={0} sx={{
                            ...glassCard({ level: 4, radius: '24px', accent: palette.rust }),
                            p: { xs: 2.5, sm: 3.5 },
                            width: '100%',
                            transition: `box-shadow 0.4s ${EASE}, transform 0.4s ${EASE}`,
                            '&:focus-within': { ...glassLift(3, palette.rust) },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0, left: '6%', right: '6%', height: '2px',
                                borderRadius: '0 0 999px 999px',
                                background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, 0.7)}, ${alpha(palette.gold, 0.7)}, ${alpha(palette.teal, 0.5)}, transparent)`,
                                backgroundSize: '300% 100%',
                                animation: 'tfEdgeShift 10s linear infinite',
                                pointerEvents: 'none',
                                zIndex: 3,
                            },
                        }}>
                            <Box sx={{ position: 'relative', zIndex: 1 }}>

                                {/* HEADER */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{
                                        ...glassCard({ level: 2, radius: '16px', accent: palette.rust }),
                                        display: { xs: 'flex', md: 'none' },
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 2.5,
                                        px: 1.5,
                                        py: 1.25,
                                    }}>
                                        <BrandLogo scope="platform" size={34} radius={12} alt="TELCOFAIR" />
                                        <Typography component="div" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, color: palette.ink, fontSize: '1.2rem' }}>
                                            TELCO<span style={{ color: palette.rust }}>FAIR</span>
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Box>
                                            <Typography component="div" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, color: palette.ink, fontSize: '1.2rem', lineHeight: 1.2 }}>
                                                {steps[activeStep]}
                                            </Typography>
                                            <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.78rem', fontWeight: 400, mt: 0.25 }}>
                                                {activeStep === 0 && 'Create your account with email verification'}
                                                {activeStep === 1 && 'Complete your professional profile'}
                                                {activeStep === 2 && 'Tell us what you\'re looking for'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ ...glassPill(palette.rust), px: 1.2, height: 22, fontSize: '0.6rem', fontWeight: 700 }}>
                                            {activeStep + 1} / 3
                                        </Box>
                                    </Box>

                                    <Box sx={{ ...glassWell('999px'), p: '2px', mt: 1.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={((activeStep + 1) / steps.length) * 100}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                backgroundColor: 'transparent',
                                                '& .MuiLinearProgress-bar': {
                                                    background: `linear-gradient(90deg, ${palette.rust}, ${alpha(palette.rust, 0.6)})`,
                                                    borderRadius: 2,
                                                    boxShadow: `0 1px 6px ${alpha(palette.rust, 0.4)}`,
                                                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* STEPPER INDICATOR */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
                                    {steps.map((label, idx) => (
                                        <React.Fragment key={idx}>
                                            <Box
                                                onClick={() => { if (idx < activeStep) { setActiveStep(idx); } }}
                                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: idx < activeStep ? 'pointer' : 'default', position: 'relative', zIndex: 2 }}
                                            >
                                                <Box sx={{ position: 'relative', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {activeStep === idx && (
                                                        <Box sx={{
                                                            position: 'absolute', width: 30, height: 30, borderRadius: '50%',
                                                            border: `2px solid ${palette.rust}`, opacity: 0,
                                                            animation: 'tfSignalWave 2s cubic-bezier(0.2,0.6,0.3,1) infinite',
                                                        }} />
                                                    )}
                                                    <motion.div
                                                        animate={{ scale: activeStep === idx ? 1.08 : 1 }}
                                                        transition={{ duration: 0.25 }}
                                                        style={{ zIndex: 1 }}
                                                    >
                                                        <Box sx={
                                                            activeStep > idx || activeStep === idx
                                                                ? glassPod(activeStep > idx ? palette.teal : palette.rust, 30, '50%')
                                                                : { ...glassWell('50%'), width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: palette.inkFaint }
                                                        }>
                                                            {activeStep > idx ? (
                                                                <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
                                                            ) : (
                                                                <Typography component="div" sx={{ fontWeight: 700, fontSize: '0.7rem', color: activeStep === idx ? '#fff' : palette.inkFaint }}>
                                                                    {idx + 1}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </motion.div>
                                                </Box>
                                                <Typography component="div" sx={{ fontSize: '0.6rem', fontWeight: activeStep === idx ? 700 : 500, color: activeStep === idx || activeStep > idx ? palette.ink : palette.inkFaint, lineHeight: 1.1 }}>
                                                    {label}
                                                </Typography>
                                            </Box>
                                            {idx < steps.length - 1 && (
                                                <Box sx={{ ...glassWell('999px'), flex: 1, height: 3, mx: 1, mb: 2, position: 'relative', overflow: 'hidden' }}>
                                                    <motion.div
                                                        animate={{ width: activeStep > idx ? '100%' : '0%' }}
                                                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            background: `linear-gradient(90deg, ${palette.rust}, ${alpha(palette.rust, 0.6)})`,
                                                            borderRadius: 999,
                                                            boxShadow: `0 1px 4px ${alpha(palette.rust, 0.4)}`,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Box>

                                <AnimatePresence>
                                    {error && (
                                        <motion.div initial={{ opacity: 0, height: 0, marginBottom: 0 }} animate={{ opacity: 1, height: 'auto', marginBottom: 16 }} exit={{ opacity: 0, height: 0, marginBottom: 0 }} transition={{ duration: 0.25 }}>
                                            <Alert severity="error" role="alert" sx={{ ...alertSx(palette.danger), fontSize: '0.78rem', py: 0.5 }}>{error}</Alert>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <Box sx={{ minHeight: 340, position: 'relative' }}>
                                    <AnimatePresence mode="wait" custom={activeStep} initial={false}>

                                        {/* STEP 0: Create Account */}
                                        {activeStep === 0 && (
                                            <motion.div key="step0" custom={activeStep} variants={slideVariants} initial="enter" animate="center" exit="exit">
                                                <Box sx={scrollAreaSx(palette.rust)}>
                                                    <motion.div custom={0} variants={fieldVariants} initial="enter" animate="center">
                                                        <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.85rem', mb: 1.5 }}>
                                                            <PersonIcon sx={{ color: palette.rust, fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                                                            Personal Information
                                                        </Typography>
                                                        
                                                        {/* Title on first step */}
                                                        <Box sx={{ mb: 1.5 }}>
                                                            <AnimatedSelectionGroup
                                                                label="Title *"
                                                                icon={<TitleIcon sx={{ color: palette.teal, fontSize: 16 }} />}
                                                                options={['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.']}
                                                                value={formData.title}
                                                                name="title"
                                                                onChange={handleInputChange}
                                                            />
                                                        </Box>
                                                        
                                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 1.5 }}>
                                                            <TextField fullWidth label="First Name *" name="firstName" value={formData.firstName} onChange={handleInputChange} required placeholder="John" variant="outlined" sx={textFieldSx} slotProps={{ input: { startAdornment: (<InputAdornment position="start"><PeopleIcon sx={{ color: palette.rust, fontSize: 16 }} /></InputAdornment>) } }} />
                                                            <TextField fullWidth label="Last Name *" name="lastName" value={formData.lastName} onChange={handleInputChange} required placeholder="Doe" variant="outlined" sx={textFieldSx} />
                                                        </Box>
                                                    </motion.div>

                                                    <motion.div custom={1} variants={fieldVariants} initial="enter" animate="center">
                                                        <TextField fullWidth label="Email Address *" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="john.doe@company.com" variant="outlined" sx={{ ...textFieldSx, mb: 1.5 }} slotProps={{ input: { startAdornment: (<InputAdornment position="start"><EmailIcon sx={{ color: palette.rust, fontSize: 16 }} /></InputAdornment>) } }} />
                                                    </motion.div>

                                                    {verificationProvider === 'whatsapp' && (
                                                        <motion.div custom={2} variants={fieldVariants} initial="enter" animate="center">
                                                            <TextField fullWidth label="WhatsApp Number *" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="+91 98765 43210" variant="outlined" sx={{ ...textFieldSx, mb: 1.5 }} slotProps={{ input: { startAdornment: (<InputAdornment position="start"><WhatsAppIcon sx={{ color: palette.green, fontSize: 17 }} /></InputAdornment>) } }} helperText="Use the number that should receive your TELCOFAIR verification code." />
                                                        </motion.div>
                                                    )}

                                                    <motion.div custom={2} variants={fieldVariants} initial="enter" animate="center">
                                                        <TextField fullWidth label="Password *" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleInputChange} required placeholder={`Min. ${passwordPolicy.minLength} characters`} variant="outlined" sx={{ ...textFieldSx, mb: 1.5 }} slotProps={{ input: { startAdornment: (<InputAdornment position="start"><LockIcon sx={{ color: palette.gold, fontSize: 16 }} /></InputAdornment>), endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label={showPassword ? 'Hide password' : 'Show password'} sx={{ color: palette.inkFaint, borderRadius: '8px', '&:hover': { color: palette.rust, background: alpha(palette.rust, 0.06) } }}>{showPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton></InputAdornment>) } }} helperText={`Min. ${passwordPolicy.minLength} chars${passwordPolicy.requireUppercase ? ', uppercase' : ''}${passwordPolicy.requireLowercase ? ', lowercase' : ''}${passwordPolicy.requireNumbers ? ', number' : ''}${passwordPolicy.requireSpecialChars ? ', special character' : ''}`} />
                                                    </motion.div>

                                                    <motion.div custom={3} variants={fieldVariants} initial="enter" animate="center">
                                                        <TextField fullWidth label="Confirm Password *" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleInputChange} required placeholder="Re-enter password" variant="outlined" sx={{ ...textFieldSx, mb: 1.5 }} slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SecurityIcon sx={{ color: palette.gold, fontSize: 16 }} /></InputAdornment>), endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} sx={{ color: palette.inkFaint, borderRadius: '8px', '&:hover': { color: palette.rust, background: alpha(palette.rust, 0.06) } }}>{showConfirmPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}</IconButton></InputAdornment>) } }} />
                                                    </motion.div>

                                                    <motion.div custom={4} variants={fieldVariants} initial="enter" animate="center">
                                                        <Box sx={{ ...glassWell('12px'), p: 1, mb: 1.5 }}>
                                                            <FormControlLabel
                                                                control={<Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} sx={{ color: alpha(palette.inkFaint, 0.7), '&.Mui-checked': { color: palette.gold }, '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
                                                                label={<Typography component="div" sx={{ fontSize: '0.72rem', color: palette.inkSoft, fontWeight: 400 }}>I agree to the <Link to="/terms" style={{ color: palette.teal, fontWeight: 600, textDecoration: 'none' }}>Terms</Link> &amp; <Link to="/privacy" style={{ color: palette.teal, fontWeight: 600, textDecoration: 'none' }}>Privacy Policy</Link></Typography>}
                                                                sx={{ m: 0 }}
                                                            />
                                                        </Box>
                                                    </motion.div>

                                                    {createdUser && !verificationSent && !isEmailVerified && verificationProvider !== 'manual' && (
                                                        <motion.div custom={5} variants={fieldVariants} initial="enter" animate="center">
                                                            <Alert severity="warning" sx={{ ...alertSx(palette.gold), mb: 1.5 }}>
                                                                <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>
                                                                    <strong>Account saved, verification delivery is pending.</strong> Your registration can be resumed safely. Try sending the verification message again.
                                                                </Typography>
                                                                <Button size="small" onClick={resendVerification} disabled={isLoading} sx={{ mt: 0.5, ...glassGhostButton(palette.teal), fontSize: '0.65rem', py: 0.3 }}>Resend verification</Button>
                                                            </Alert>
                                                        </motion.div>
                                                    )}

                                                    {verificationSent && !isEmailVerified && (
                                                        <motion.div custom={5} variants={fieldVariants} initial="enter" animate="center">
                                                            <Alert severity="info" sx={{ ...alertSx(palette.teal), mb: 1.5 }}>
                                                                <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>
                                                                    <strong>{verificationProvider === 'whatsapp' ? 'WhatsApp verification sent!' : verificationProvider === 'manual' ? 'Verification pending.' : 'Verification sent!'}</strong>{' '}
                                                                    {verificationProvider === 'whatsapp' ? 'Enter the OTP below.' : verificationProvider === 'manual' ? 'TELCOFAIR Super Admin must verify this account.' : 'Check your inbox and complete the verification link.'}
                                                                    {verificationProvider !== 'whatsapp' && verificationCheckCount > 0 && ` (Checked ${verificationCheckCount} times)`}
                                                                </Typography>
                                                                {verificationProvider === 'whatsapp' && (
                                                                    <Box sx={{ display: 'flex', gap: .8, mt: 1, flexWrap: 'wrap' }}>
                                                                        <TextField size="small" label="WhatsApp OTP" value={registrationWhatsAppOtp} onChange={(e) => setRegistrationWhatsAppOtp(e.target.value.replace(/\D/g, '').slice(0, 8))} sx={{ flex: 1, minWidth: 160 }} />
                                                                        <Button size="small" onClick={verifyRegistrationWhatsAppOtp} disabled={isLoading} startIcon={<WhatsAppIcon />} sx={{ ...glassButton(palette.green, palette.pine), px: 1.6 }}>Verify OTP</Button>
                                                                    </Box>
                                                                )}
                                                                {verificationProvider !== 'manual' && (
                                                                    <Button size="small" onClick={resendVerification} disabled={isLoading} sx={{ mt: 0.5, ...glassGhostButton(palette.teal), fontSize: '0.65rem', py: 0.3 }}>Resend</Button>
                                                                )}
                                                            </Alert>
                                                        </motion.div>
                                                    )}

                                                    {isEmailVerified && (
                                                        <motion.div custom={6} variants={fieldVariants} initial="enter" animate="center">
                                                            <Alert severity="success" sx={{ ...alertSx(palette.green), mb: 1.5 }}>
                                                                <Typography variant="body2" sx={{ fontSize: '0.72rem' }}>
                                                                    <strong>✅ Account verified!</strong> Click Continue to complete your profile.
                                                                </Typography>
                                                            </Alert>
                                                        </motion.div>
                                                    )}
                                                </Box>
                                            </motion.div>
                                        )}

                                        {/* STEP 1: Complete Profile */}
                                        {activeStep === 1 && (
                                            <motion.div key="step1" custom={activeStep} variants={slideVariants} initial="enter" animate="center" exit="exit">
                                                <Box sx={scrollAreaSx(palette.teal)}>
                                                    <motion.div custom={0} variants={fieldVariants} initial="enter" animate="center">
                                                        <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.85rem', mb: 1.5 }}>
                                                            <TitleIcon sx={{ color: palette.teal, fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                                                            Professional Details
                                                        </Typography>

                                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 1.5 }}>
                                                            <Box sx={{ flex: 1 }}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Full Name"
                                                                    value={`${formData.firstName} ${formData.lastName}`}
                                                                    disabled
                                                                    variant="outlined"
                                                                    sx={{
                                                                        ...textFieldSx,
                                                                        '& .MuiInputBase-root.Mui-disabled': {
                                                                            bgcolor: alpha(palette.canvasAlt, 0.4),
                                                                            '& input': { color: palette.inkSoft },
                                                                        }
                                                                    }}
                                                                    slotProps={{ input: { startAdornment: (<InputAdornment position="start"><PersonIcon sx={{ color: palette.inkSoft, fontSize: 16 }} /></InputAdornment>) } }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </motion.div>

                                                    {/* Profile Picture Upload */}
                                                    <motion.div custom={1} variants={fieldVariants} initial="enter" animate="center">
                                                        <Box sx={{ mb: 1.5 }}>
                                                            <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.5 }}>
                                                                Profile Picture
                                                            </Typography>
                                                            <ImageUploadField
                                                                label="Photo"
                                                                icon={<PhotoCameraIcon />}
                                                                preview={profileImagePreview}
                                                                onUpload={handleProfileImageUpload}
                                                                onRemove={handleProfileImageRemove}
                                                                size={64}
                                                            />
                                                        </Box>
                                                    </motion.div>

                                                    <motion.div custom={2} variants={fieldVariants} initial="enter" animate="center">
                                                        <Box sx={{ mb: 1.5 }}>
                                                            <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.5 }}>Country Located *</Typography>
                                                            <Button fullWidth onClick={(e) => setCountrySearchAnchorEl(e.currentTarget)} sx={{ ...pickerButtonSx(Boolean(formData.country)), justifyContent: 'space-between' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {formData.country ? (
                                                                        <><Typography component="span" sx={{ fontSize: 18 }}>{formData.country.flag}</Typography><Typography component="span" sx={{ fontWeight: 500, color: palette.ink, fontSize: '0.8rem' }}>{formData.country.name}</Typography></>
                                                                    ) : (
                                                                        <><FlagIcon sx={{ color: palette.gold, fontSize: 18 }} /><Typography component="span" sx={{ color: palette.inkFaint, fontSize: '0.8rem' }}>Select Country</Typography></>
                                                                    )}
                                                                </Box>
                                                                <KeyboardArrowDownIcon sx={{ color: palette.inkFaint, fontSize: 16 }} />
                                                            </Button>
                                                            <Popover
                                                                open={Boolean(countrySearchAnchorEl)}
                                                                anchorEl={countrySearchAnchorEl}
                                                                onClose={() => setCountrySearchAnchorEl(null)}
                                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                                slotProps={{ paper: { sx: popoverPaperSx(340) } }}
                                                            >
                                                                <Box sx={{ p: 1, borderBottom: `1px solid ${alpha(palette.ink, 0.06)}` }}>
                                                                    <TextField fullWidth size="small" placeholder="Search..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} sx={searchFieldSx} slotProps={{ input: { startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: palette.inkSoft }} /></InputAdornment>), endAdornment: countrySearch && (<InputAdornment position="end"><IconButton size="small" onClick={() => setCountrySearch('')}><CloseIcon sx={{ fontSize: 12 }} /></IconButton></InputAdornment>) } }} />
                                                                </Box>
                                                                <MenuList sx={{ maxHeight: 300, overflowY: 'auto', py: 0 }}>
                                                                    {countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase())).map((country) => (
                                                                        <MenuItem key={country.code} onClick={() => { handleSelectCountry(country); setCountrySearchAnchorEl(null); }} sx={{ ...countryItemSx, py: 0.8, px: 1.5 }}>
                                                                            <Typography component="div" sx={{ fontSize: 18 }}>{country.flag}</Typography>
                                                                            <Typography component="div" sx={{ flex: 1, fontSize: '0.78rem', fontWeight: formData.country?.code === country.code ? 600 : 400, color: palette.ink }}>{country.name}</Typography>
                                                                            <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.72rem' }}>{country.dialCode}</Typography>
                                                                            {formData.country?.code === country.code && (<CheckCircleIcon sx={{ color: palette.rust, fontSize: 14 }} />)}
                                                                        </MenuItem>
                                                                    ))}
                                                                    {countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase())).length === 0 && (
                                                                        <Box sx={{ p: 2, textAlign: 'center' }}><Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.75rem' }}>No countries found</Typography></Box>
                                                                    )}
                                                                </MenuList>
                                                            </Popover>
                                                        </Box>
                                                    </motion.div>

                                                    <motion.div custom={3} variants={fieldVariants} initial="enter" animate="center">
                                                        <Box sx={{ mb: 1.5 }}>
                                                            <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 0.5 }}>
                                                                Company * {registrationMode === 'join' ? '(Select from list)' : '(Register new company)'}
                                                            </Typography>

                                                            {registrationMode === 'join' ? (
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Autocomplete
                                                                            options={companies}
                                                                            getOptionLabel={(option) => `${option.companyName} (${option.country || 'N/A'})`}
                                                                            loading={loadingCompanies}
                                                                            value={selectedCompany}
                                                                            onChange={(e, newValue) => handleCompanySelect(newValue)}
                                                                            slotProps={{ paper: { sx: { ...glassCard({ level: 3, radius: '14px' }), mt: 0.5, overflow: 'hidden' } } }}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label="Search company *"
                                                                                    required
                                                                                    variant="outlined"
                                                                                    sx={{ ...textFieldSx, mb: 0 }}
                                                                                    placeholder="Search..."
                                                                                />
                                                                            )}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props} key={option.id}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.25 }}>
                                                                                        <Typography component="div" sx={{ fontWeight: 600, color: palette.ink, fontSize: '0.8rem' }}>{option.companyName}</Typography>
                                                                                        <Typography component="div" sx={{ color: palette.inkFaint, fontSize: '0.65rem' }}>{option.country || 'N/A'}</Typography>
                                                                                        {option.isVerified && <VerifiedIcon sx={{ color: palette.green, fontSize: 12, ml: 'auto' }} />}
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            noOptionsText={loadingCompanies ? 'Loading...' : 'No companies found.'}
                                                                            sx={{ '& .MuiOutlinedInput-root': { paddingLeft: '4px !important' } }}
                                                                        />
                                                                    </Box>
                                                                    <Button
                                                                        onClick={handleCreateCompany}
                                                                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                                                        sx={{ ...glassButton(palette.teal, palette.tealDeep), fontSize: '0.7rem', whiteSpace: 'nowrap', minWidth: 'auto', height: '44px', px: 2, borderRadius: '12px' }}
                                                                    >
                                                                        Create
                                                                    </Button>
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            <TextField
                                                                                fullWidth
                                                                                label="Company Name *"
                                                                                name="companyName"
                                                                                value={formData.companyName}
                                                                                onChange={handleInputChange}
                                                                                required
                                                                                placeholder="Enter company name"
                                                                                variant="outlined"
                                                                                sx={textFieldSx}
                                                                                slotProps={{ input: {
                                                                                    startAdornment: (
                                                                                        <InputAdornment position="start">
                                                                                            <BusinessIcon sx={{ color: palette.teal, fontSize: 16 }} />
                                                                                        </InputAdornment>
                                                                                    )
                                                                                } }}
                                                                            />
                                                                        </Box>
                                                                        <Button
                                                                            onClick={() => setRegistrationMode('join')}
                                                                            startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                                                                            sx={{ ...glassGhostButton(palette.teal), fontSize: '0.7rem', height: '44px', px: 2 }}
                                                                        >
                                                                            Join
                                                                        </Button>
                                                                    </Box>
                                                                    <TextField
                                                                        fullWidth
                                                                        label="Company Website (HTTPS URL) *"
                                                                        name="companyWebsite"
                                                                        value={formData.companyWebsite || ''}
                                                                        onChange={handleInputChange}
                                                                        required
                                                                        placeholder="https://www.yourcompany.com"
                                                                        variant="outlined"
                                                                        sx={textFieldSx}
                                                                        slotProps={{ input: {
                                                                            startAdornment: (
                                                                                <InputAdornment position="start">
                                                                                    <LanguageIcon sx={{ color: palette.purple, fontSize: 16 }} />
                                                                                </InputAdornment>
                                                                            )
                                                                        } }}
                                                                    />
                                                                    <AnimatedSelectionGroup
                                                                        label="Company Type *"
                                                                        icon={<DomainIcon sx={{ color: palette.blue, fontSize: 16 }} />}
                                                                        options={companyTypes}
                                                                        value={formData.companyType || ''}
                                                                        name="companyType"
                                                                        onChange={handleInputChange}
                                                                    />

                                                                    {/* Company Logo and Cover Upload */}
                                                                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                                        <ImageUploadField
                                                                            label="Logo"
                                                                            icon={<BusinessIcon />}
                                                                            preview={companyLogoPreview}
                                                                            onUpload={handleCompanyLogoUpload}
                                                                            onRemove={handleCompanyLogoRemove}
                                                                            size={64}
                                                                        />
                                                                        <ImageUploadField
                                                                            label="Cover"
                                                                            icon={<ImageIcon />}
                                                                            preview={companyCoverPreview}
                                                                            onUpload={handleCompanyCoverUpload}
                                                                            onRemove={handleCompanyCoverRemove}
                                                                            size={64}
                                                                        />
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </motion.div>

                                                    <motion.div custom={4} variants={fieldVariants} initial="enter" animate="center">
                                                        <AnimatedSelectionGroup
                                                            label="Business Title *"
                                                            icon={<WorkIcon sx={{ color: palette.teal, fontSize: 16 }} />}
                                                            options={businessTitles}
                                                            value={formData.businessTitle}
                                                            name="businessTitle"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={5} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Specialization *"
                                                            icon={<StarsIcon sx={{ color: palette.gold, fontSize: 16 }} />}
                                                            options={specializations}
                                                            value={formData.specialization}
                                                            name="specialization"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={6} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Primary Region of Availability *"
                                                            icon={<PublicIcon sx={{ color: palette.blue, fontSize: 16 }} />}
                                                            options={regions}
                                                            value={formData.primaryRegionAvailability}
                                                            name="primaryRegionAvailability"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={7} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Primary Services Provided *"
                                                            icon={<SellIcon sx={{ color: palette.green, fontSize: 16 }} />}
                                                            options={services}
                                                            value={formData.primaryServiceProvided}
                                                            name="primaryServiceProvided"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={8} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Secondary Region of Availability"
                                                            icon={<MapIcon sx={{ color: '#1b5e20', fontSize: 16 }} />}
                                                            options={regions}
                                                            value={formData.secondaryRegionAvailability}
                                                            name="secondaryRegionAvailability"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={9} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Secondary Services Provided"
                                                            icon={<CategoryIcon sx={{ color: palette.purple, fontSize: 16 }} />}
                                                            options={services}
                                                            value={formData.secondaryServiceProvided}
                                                            name="secondaryServiceProvided"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    {/* NEW: LinkedIn Profile Field */}
                                                    <motion.div custom={10} variants={fieldVariants} initial="enter" animate="center">
                                                        <TextField
                                                            fullWidth
                                                            label="LinkedIn Profile URL (Optional)"
                                                            name="linkedinProfile"
                                                            value={formData.linkedinProfile}
                                                            onChange={handleInputChange}
                                                            placeholder="https://linkedin.com/in/yourusername"
                                                            variant="outlined"
                                                            sx={textFieldSx}
                                                            error={!!linkedinError}
                                                            helperText={linkedinError || "Enter your public LinkedIn profile URL (optional)"}
                                                            slotProps={{ input: {
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <LinkedInIcon sx={{ color: palette.linkedin, fontSize: 18 }} />
                                                                    </InputAdornment>
                                                                ),
                                                                endAdornment: formData.linkedinProfile && (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => {
                                                                                setFormData({ ...formData, linkedinProfile: '' });
                                                                                setLinkedinError('');
                                                                            }}
                                                                            sx={{ color: palette.inkFaint }}
                                                                        >
                                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            } }}
                                                        />
                                                    </motion.div>

                                                    {/* Company Section Border */}
                                                    {registrationMode === 'create' && (
                                                        <motion.div custom={11} variants={fieldVariants} initial="enter" animate="center">
                                                            <Box sx={{
                                                                border: `1px solid ${alpha(palette.ink, 0.08)}`,
                                                                borderRadius: '12px',
                                                                p: 2,
                                                                mt: 0.5,
                                                                mb: 1,
                                                                bgcolor: alpha(palette.canvasAlt, 0.3),
                                                            }}>
                                                                <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.78rem', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <AdminPanelSettingsIcon sx={{ fontSize: 16, color: palette.rust }} />
                                                                    Company Registration
                                                                </Typography>
                                                                <Alert severity="info" icon={<AdminPanelSettingsIcon />} sx={{ ...alertSx(palette.teal), mb: 0.5 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: palette.inkSoft, fontSize: '0.72rem' }}>
                                                                        <strong>You will be the admin of this company.</strong> Full access to manage profile, team, and subscriptions.
                                                                    </Typography>
                                                                </Alert>
                                                            </Box>
                                                        </motion.div>
                                                    )}
                                                </Box>
                                            </motion.div>
                                        )}

                                        {/* STEP 2: Buying Interests + Subscription */}
                                        {activeStep === 2 && (
                                            <motion.div key="step2" custom={activeStep} variants={slideVariants} initial="enter" animate="center" exit="exit">
                                                <Box sx={scrollAreaSx(palette.gold)}>
                                                    <motion.div custom={0} variants={fieldVariants} initial="enter" animate="center">
                                                        <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.85rem', mb: 1.5 }}>
                                                            <ShoppingCartIcon sx={{ color: palette.gold, fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                                                            I am interested in buying
                                                        </Typography>

                                                        <MultiSelectAnimatedGroup
                                                            label="Preferred Region *"
                                                            icon={<PublicIcon sx={{ color: palette.blue, fontSize: 16 }} />}
                                                            options={regions}
                                                            value={formData.preferredRegion}
                                                            name="preferredRegion"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={1} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Primary Services Interested in Buying *"
                                                            icon={<ShoppingCartIcon sx={{ color: palette.gold, fontSize: 16 }} />}
                                                            options={services}
                                                            value={formData.primaryServiceBuying}
                                                            name="primaryServiceBuying"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={2} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Secondary Preferred Region"
                                                            icon={<MapIcon sx={{ color: '#1b5e20', fontSize: 16 }} />}
                                                            options={regions}
                                                            value={formData.secondaryPreferredRegion}
                                                            name="secondaryPreferredRegion"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    <motion.div custom={3} variants={fieldVariants} initial="enter" animate="center">
                                                        <MultiSelectAnimatedGroup
                                                            label="Secondary Services Interested in Buying"
                                                            icon={<CategoryIcon sx={{ color: palette.purple, fontSize: 16 }} />}
                                                            options={services}
                                                            value={formData.secondaryServiceBuying}
                                                            name="secondaryServiceBuying"
                                                            onChange={handleInputChange}
                                                        />
                                                    </motion.div>

                                                    {/* Subscription Section - Only for Company Registration */}
                                                    {registrationMode === 'create' && (
                                                        <motion.div custom={4} variants={fieldVariants} initial="enter" animate="center">
                                                            <Box sx={{
                                                                border: `1px solid ${alpha(palette.ink, 0.08)}`,
                                                                borderRadius: '12px',
                                                                p: 2,
                                                                mt: 2,
                                                                bgcolor: alpha(palette.canvasAlt, 0.3),
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                                                    <Typography component="div" sx={{ fontWeight: 600, color: palette.inkLabel, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <SubscriptionsIcon sx={{ fontSize: 18, color: palette.teal }} />
                                                                        Select Your Subscription Package
                                                                        {subscriptionSettings.paymentRequired && (
                                                                            <Box sx={{ ...glassPill(palette.teal, true), px: 0.75, height: 16, fontSize: '0.5rem' }}>Payment Required</Box>
                                                                        )}
                                                                    </Typography>
                                                                    {selectedPackage && (
                                                                        <Box sx={{ ...glassPill(palette.green, true), px: 0.75, height: 16, fontSize: '0.5rem' }}>
                                                                            <CheckIcon sx={{ fontSize: 10 }} /> Selected
                                                                        </Box>
                                                                    )}
                                                                </Box>

                                                                <SubscriptionSelection
                                                                    packages={packages}
                                                                    selectedPackage={selectedPackage}
                                                                    setSelectedPackage={setSelectedPackage}
                                                                    currency={subscriptionSettings.currency}
                                                                    loading={loadingPackages}
                                                                />

                                                                {!selectedPackage && (
                                                                    <Alert severity="warning" sx={{ ...alertSx(palette.warning), mt: 1 }}>
                                                                        <Typography variant="body2" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                                                                            ⚠️ Please select a subscription package to continue
                                                                        </Typography>
                                                                    </Alert>
                                                                )}

                                                                {subscriptionSettings.paymentRequired && selectedPackage && selectedPackage.price > 0 && (
                                                                    <Alert severity="info" sx={{ ...alertSx(palette.teal), mt: 1.5 }}>
                                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>
                                                                            💳 The company subscription payment of <strong>{subscriptionSettings.currency === 'EUR' ? '€' : '$'}{selectedPackage.price}</strong> is required to activate this new company.
                                                                            Individual users joining an existing subscribed company do not pay.
                                                                        </Typography>
                                                                    </Alert>
                                                                )}
                                                            </Box>
                                                        </motion.div>
                                                    )}
                                                </Box>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Box>

                                {activeStep > 0 && auth.currentUser && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .7, mt: 2, px: .25 }}>
                                        <CheckCircleIcon sx={{ fontSize: 15, color: palette.green }} />
                                        <Typography sx={{ fontSize: '.68rem', color: palette.inkSoft, fontWeight: 550 }}>
                                            {restoringProgress ? 'Restoring your saved registration…' : progressSavedAt ? 'Progress saved automatically — you can safely continue later.' : 'Your progress is saved automatically as you complete each step.'}
                                        </Typography>
                                    </Box>
                                )}

                                {/* ACTION BUTTONS */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.6, pt: 2.1, borderTop: `1px solid ${alpha(palette.ink, 0.06)}`, gap: 1.5 }}>
                                    {activeStep > 0 ? (
                                        <Button onClick={() => setActiveStep(activeStep - 1)} disabled={isLoading} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 0.6, fontSize: '0.75rem', '&.Mui-disabled': { opacity: 0.3 } }}>Back</Button>
                                    ) : <Box />}

                                    {activeStep < 2 ? (
                                        <Button
                                            onClick={async () => {
                                                if (activeStep === 0) {
                                                    if (!isEmailVerified) {
                                                        createAccountAndSendVerification();
                                                        return;
                                                    }
                                                }
                                                if (activeStep === 0 && isEmailVerified) {
                                                    await persistRegistrationStage('profile_completion', { silent: true });
                                                }
                                                if (activeStep === 1) {
                                                    const validationError = validateStep2();
                                                    if (validationError) {
                                                        setError(validationError);
                                                        return;
                                                    }
                                                    setError('');
                                                    await persistRegistrationStage('business_interests', { silent: true });
                                                }
                                                setActiveStep(activeStep + 1);
                                            }}
                                            endIcon={activeStep === 0 && !isEmailVerified ? <EmailIcon sx={{ fontSize: 16 }} /> : <ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                            sx={{ ...glassButton(palette.teal, palette.tealDeep), px: 3, py: 0.7, fontSize: '0.8rem' }}
                                            disabled={isLoading || loadingSettings || (activeStep === 0 && !formData.email)}
                                        >
                                            {activeStep === 0 && !isEmailVerified ? (verificationProvider === 'whatsapp' ? 'Create & Send WhatsApp OTP' : verificationProvider === 'manual' ? 'Create Account' : 'Create & Send Verification') : 'Continue'}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleRegistration}
                                            disabled={isLoading || isSubmitting || (registrationMode === 'create' && !selectedPackage)}
                                            endIcon={isLoading || isSubmitting ? <CircularProgress size={16} color="inherit" /> : <RocketIcon sx={{ fontSize: 16 }} />}
                                            sx={{ ...glassButton(palette.green, palette.greenDeep), px: 3.5, py: 0.7, fontSize: '0.8rem' }}
                                        >
                                            {isLoading || isSubmitting ? 'Processing...' : 'Complete Registration'}
                                        </Button>
                                    )}
                                </Box>

                                <Box sx={{ ...glassCard({ level: 0, radius: '12px' }), textAlign: 'center', mt: 2, p: 1 }}>
                                    <Typography component="div" sx={{ color: palette.inkSoft, fontSize: '0.72rem', fontWeight: 400 }}>
                                        Already have an account? <Link to="/login" style={{ color: palette.teal, fontWeight: 600, textDecoration: 'none' }}>Sign In</Link>
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </motion.div>
                </Box>
            </Box>

            {/* Payment Dialog */}
            <ExtraSeatPaymentDialog
                open={Boolean(extraSeatPayment)}
                payment={extraSeatPayment}
                onClose={() => setExtraSeatPayment(null)}
                onSubmitted={async () => {
                    const data = registrationData;
                    setExtraSeatPayment(null);
                    if (data) await completeRegistration({ ...data, extraSeatPaymentRequired: true });
                }}
            />

            <SubscriptionPaymentDialog
                open={showPaymentDialog}
                onClose={handlePaymentCancel}
                onSuccess={handlePaymentSuccess}
                onPending={handleManualPaymentPending}
                selectedPackage={selectedPackage}
                currency={subscriptionSettings.currency}
                companyName={registrationMode === 'create' ? formData.companyName : selectedCompany?.companyName || formData.companyName}
                email={auth.currentUser?.email || formData.email}
                phone={`${formData.dialCode || ''}${formData.phone || ''}`}
                userId={auth.currentUser?.uid || null}
                companyId={registrationData?.companyId || resumeProgress?.companyId || resumeProgress?.draft?.selectedCompanyId || null}
            />
        </Box>
    );
}

export default Register;
